import { ArrowRightLeft, Lock, MessageSquare, RotateCcw, Send, Shield, Shuffle, Sparkles, Trophy, Star, Info, Share2, Smartphone, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import {
  trackAppInterestSubmitted,
  trackDraftCompleted,
  trackDraftPick,
  trackFeedbackOpened,
  trackFeedbackSubmitted,
  trackFavoriteClubSelected,
  trackFormationSelected,
  trackModeSelected,
  trackShareAction,
} from "./analytics";
import { CookieConsentBanner } from "./CookieConsentBanner";
import { getCardStats, getPlayerProfile } from "./data/playerProfiles";
import { dataNotice, players, type Club, type PlayerSeason } from "./data/players";
import { defaultFormationId, eraLabels, formations, modes } from "./game/config";
import { createPrompt, getMixedDraftOffers, simulateSeason } from "./game/engine";
import { translations, type Language } from "./game/i18n";
import { isSlotCompatible } from "./game/positions";
import type { DraftOffer, DraftPick, DraftRarity, Formation, GameMode, LineupSlot, SeasonResult } from "./game/types";

const storageKey = "serie-a-38-0-session";
const siteUrl = "https://www.serie-a-38-0.com/";

type DraftChallengeId = "standard" | "tactical";
type ModeStoryId = "modern" | "nostalgia";
type ModeDifficultyId = "easy" | "medium" | "hard";

interface ModeStoryDefinition {
  id: ModeStoryId;
  defaultDifficulty: ModeDifficultyId;
  difficulties: ModeDifficultyId[];
  modeIds: Partial<Record<ModeDifficultyId, string>>;
}

const modeStories: ModeStoryDefinition[] = [
  {
    id: "modern",
    defaultDifficulty: "easy",
    difficulties: ["easy", "hard"],
    modeIds: { easy: "draft", hard: "draft-hard" },
  },
  {
    id: "nostalgia",
    defaultDifficulty: "medium",
    difficulties: ["medium", "hard"],
    modeIds: { medium: "nostalgia", hard: "nostalgia-hard" },
  },
];

const languageOptions: Array<{ code: Language; label: string; flag: string; path: string; locale: string; nativeName: string }> = [
  { code: "it", label: "IT", flag: "🇮🇹", path: "/it/", locale: "it_IT", nativeName: "Italiano" },
  { code: "en", label: "EN", flag: "🇬🇧", path: "/en/", locale: "en_US", nativeName: "English" },
  { code: "es", label: "ES", flag: "🇪🇸", path: "/es/", locale: "es_ES", nativeName: "Español" },
  { code: "pt-BR", label: "BR", flag: "🇧🇷", path: "/pt-br/", locale: "pt_BR", nativeName: "Português BR" },
  { code: "fr", label: "FR", flag: "🇫🇷", path: "/fr/", locale: "fr_FR", nativeName: "Français" },
];

const languagePaths = new Map(languageOptions.map((option) => [option.code, option.path]));
const languageLocales = new Map(languageOptions.map((option) => [option.code, option.locale]));
const pathLanguages = new Map(languageOptions.map((option) => [option.path.replace(/^\/+|\/+$/g, ""), option.code]));
pathLanguages.set("", "it");

interface SavedSession {
  modeId: string;
  formationId?: string;
  challengeId?: DraftChallengeId;
  favoriteClub?: Club;
  draftSeed?: string;
  pickedPlayerIds: string[];
  pickedSlotIds?: string[];
}

interface ParsedSession extends SavedSession {
  sharedOpponent?: SharedOpponent;
}

interface InitialSession {
  mode: GameMode;
  formation: Formation;
  challengeId: DraftChallengeId;
  favoriteClub: Club;
  draftSeed: string;
  picks: DraftPick[];
  sharedOpponent?: SharedOpponent;
}

interface SharedRunPayload {
  v: 1;
  seed: string;
  modeId: string;
  formationId: string;
  challengeId: DraftChallengeId;
  favoriteClub?: Club;
  opponent?: SharedOpponent;
}

interface SharedOpponent {
  picks: Array<{ playerId: string; slotId: string }>;
  result: SharedOpponentResult;
}

interface SharedOpponentResult {
  wins: number;
  draws: number;
  losses: number;
  points: number;
  rating: number;
  chemistry: number;
  attackBonus: number;
  topPlayer: string;
}

interface TacticalChallengeState {
  id: string;
  title: string;
  body: string;
  current: number;
  target: number;
  complete: boolean;
  progress: string;
}

type FeedbackSource = "footer" | "result";
type FeedbackStatus = "idle" | "submitting" | "sent" | "error";
type SurveyPlatform = "ios" | "android" | "both";
type SurveyStatus = "idle" | "submitting" | "sent" | "error";

const SLOT_COORDS: Record<string, { x: number; y: number }> = {
  gk: { x: 50, y: 88 },
  lb: { x: 15, y: 70 },
  lcb: { x: 25, y: 72 },
  cb1: { x: 37, y: 71 },
  cb: { x: 50, y: 73 },
  cb2: { x: 63, y: 71 },
  rcb: { x: 75, y: 72 },
  rb: { x: 85, y: 70 },
  lwb: { x: 12, y: 48 },
  cm1: { x: 26, y: 46 },
  regista: { x: 50, y: 53 },
  cm2: { x: 74, y: 46 },
  rwb: { x: 88, y: 48 },
  lm: { x: 17, y: 45 },
  rm: { x: 83, y: 45 },
  dm1: { x: 38, y: 53 },
  dm2: { x: 62, y: 53 },
  am1: { x: 35, y: 28 },
  am2: { x: 65, y: 28 },
  trequartista: { x: 50, y: 30 },
  lw: { x: 18, y: 20 },
  st: { x: 50, y: 15 },
  st1: { x: 38, y: 16 },
  st2: { x: 62, y: 16 },
  rw: { x: 82, y: 20 },
};

const chemistryLinks433 = [
  { from: "gk", to: "cb1" }, { from: "gk", to: "cb2" },
  { from: "cb1", to: "cb2" }, { from: "cb1", to: "lb" }, { from: "cb2", to: "rb" },
  { from: "cb1", to: "regista" }, { from: "cb2", to: "regista" },
  { from: "lb", to: "cm1" }, { from: "rb", to: "cm2" },
  { from: "cm1", to: "regista" }, { from: "cm2", to: "regista" },
  { from: "cm1", to: "lw" }, { from: "cm2", to: "rw" },
  { from: "regista", to: "st" }, { from: "lw", to: "st" }, { from: "rw", to: "st" },
];

const CHEMISTRY_LINKS_BY_FORMATION: Record<string, { from: string; to: string }[]> = {
  "433": chemistryLinks433,
  "4312": [
    { from: "gk", to: "cb1" }, { from: "gk", to: "cb2" },
    { from: "cb1", to: "cb2" }, { from: "cb1", to: "lb" }, { from: "cb2", to: "rb" },
    { from: "cb1", to: "regista" }, { from: "cb2", to: "regista" },
    { from: "cm1", to: "regista" }, { from: "cm2", to: "regista" },
    { from: "cm1", to: "trequartista" }, { from: "cm2", to: "trequartista" },
    { from: "regista", to: "trequartista" },
    { from: "trequartista", to: "st1" }, { from: "trequartista", to: "st2" }, { from: "st1", to: "st2" },
  ],
  "442": [
    { from: "gk", to: "cb1" }, { from: "gk", to: "cb2" },
    { from: "cb1", to: "cb2" }, { from: "cb1", to: "lb" }, { from: "cb2", to: "rb" },
    { from: "lb", to: "lm" }, { from: "rb", to: "rm" },
    { from: "lm", to: "cm1" }, { from: "cm1", to: "cm2" }, { from: "cm2", to: "rm" },
    { from: "lm", to: "st1" }, { from: "rm", to: "st2" }, { from: "cm1", to: "st1" }, { from: "cm2", to: "st2" },
    { from: "st1", to: "st2" },
  ],
  "352": [
    { from: "gk", to: "lcb" }, { from: "gk", to: "cb" }, { from: "gk", to: "rcb" },
    { from: "lcb", to: "cb" }, { from: "cb", to: "rcb" },
    { from: "lcb", to: "lwb" }, { from: "rcb", to: "rwb" },
    { from: "lwb", to: "cm1" }, { from: "cm1", to: "regista" }, { from: "regista", to: "cm2" }, { from: "cm2", to: "rwb" },
    { from: "cm1", to: "st1" }, { from: "regista", to: "st1" }, { from: "regista", to: "st2" }, { from: "cm2", to: "st2" },
    { from: "st1", to: "st2" },
  ],
  "4321": [
    { from: "gk", to: "cb1" }, { from: "gk", to: "cb2" },
    { from: "cb1", to: "cb2" }, { from: "cb1", to: "lb" }, { from: "cb2", to: "rb" },
    { from: "cb1", to: "regista" }, { from: "cb2", to: "regista" },
    { from: "lb", to: "cm1" }, { from: "rb", to: "cm2" },
    { from: "cm1", to: "regista" }, { from: "cm2", to: "regista" },
    { from: "cm1", to: "am1" }, { from: "regista", to: "am1" }, { from: "regista", to: "am2" }, { from: "cm2", to: "am2" },
    { from: "am1", to: "st" }, { from: "am2", to: "st" }, { from: "am1", to: "am2" },
  ],
  "4231": [
    { from: "gk", to: "cb1" }, { from: "gk", to: "cb2" },
    { from: "cb1", to: "cb2" }, { from: "cb1", to: "lb" }, { from: "cb2", to: "rb" },
    { from: "lb", to: "dm1" }, { from: "rb", to: "dm2" },
    { from: "dm1", to: "dm2" }, { from: "dm1", to: "am1" }, { from: "dm1", to: "trequartista" },
    { from: "dm2", to: "trequartista" }, { from: "dm2", to: "am2" },
    { from: "am1", to: "trequartista" }, { from: "trequartista", to: "am2" },
    { from: "am1", to: "st" }, { from: "trequartista", to: "st" }, { from: "am2", to: "st" },
  ],
};

const rarityLabel = {
  comune: "Comune",
  rara: "Rara",
  campione: "Campione",
  icona: "Icona",
};

const dataLevelLabel: Record<string, string> = {
  sourced: "Fonte dati",
  "curated-high": "Profilo storico",
  "curated-medium": "Profilo curato",
  derived: "Profilo derivato",
};

// Emoji flags lookup
function getCountryEmoji(country: string): string {
  const flags: Record<string, string> = {
    "Italia": "🇮🇹",
    "Argentina": "🇦🇷",
    "Brasile": "🇧🇷",
    "Francia": "🇫🇷",
    "Repubblica Ceca": "🇨🇿",
    "Portogallo": "🇵🇹",
    "Spagna": "🇪🇸",
    "Uruguay": "🇺🇾",
    "Paesi Bassi": "🇳🇱",
    "Svezia": "🇸🇪",
    "Ucraina": "🇺🇦",
    "Nigeria": "🇳🇬",
    "Georgia": "🇬🇪",
    "Germania": "🇩🇪",
    "Belgio": "🇧🇪",
    "Cile": "🇨🇱",
    "Liberia": "🇱🇷",
    "Colombia": "🇨🇴",
    "Croazia": "🇭🇷",
    "Serbia": "🇷🇸",
    "Russia": "🇷🇺",
    "Macedonia del Nord": "🇲🇰",
    "Macedonia": "🇲🇰",
    "Polonia": "🇵🇱",
    "Inghilterra": "🏴󠁧󠁢󠁥󠁮󠁧󠁿",
    "Camerun": "🇨🇲",
    "Costa d'Avorio": "🇨🇮",
    "Ghana": "🇬🇭",
    "Slovacchia": "🇸🇰",
    "Svizzera": "🇨🇭",
    "Slovenia": "🇸🇮",
    "Bosnia ed Erzegovina": "🇧🇦",
    "Bosnia": "🇧🇦",
    "Montenegro": "🇲🇪",
    "Albania": "🇦🇱",
    "Austria": "🇦🇹",
    "Danimarca": "🇩🇰",
    "Grecia": "🇬🇷",
    "Turchia": "🇹🇷",
    "Romania": "🇷🇴",
    "Galles": "🏴󠁧󠁢󠁷󠁬󠁳󠁿",
    "Senegal": "🇸🇳",
    "Marocco": "🇲🇦",
    "Egitto": "🇪🇬",
    "Angola": "🇦🇴",
    "Giappone": "🇯🇵",
    "Corea del Sud": "🇰🇷",
    "Stati Uniti": "🇺🇸",
    "Messico": "🇲🇽",
    "Canada": "🇨🇦",
  };
  return flags[country] || "🏳️";
}

// Pure CSS club badge styles
function getClubBadgeStyle(club: Club): React.CSSProperties {
  const styles: Record<Club, React.CSSProperties> = {
    Bari: { background: "linear-gradient(135deg, #e51b23 52%, #fff 48%)" },
    Juventus: { background: "linear-gradient(90deg, #000 50%, #fff 50%)" },
    Milan: { background: "linear-gradient(90deg, #e31b23 50%, #000 50%)" },
    Inter: { background: "linear-gradient(90deg, #0053a0 50%, #000 50%)" },
    Napoli: { background: "#1b93d6" },
    Roma: { background: "linear-gradient(135deg, #7a151b 50%, #f7b717 50%)" },
    Lazio: { background: "linear-gradient(135deg, #8bc9e7 60%, #fff 40%)" },
    Parma: { background: "linear-gradient(90deg, #00367c 50%, #f8c300 50%)" },
    Sampdoria: { background: "linear-gradient(180deg, #0053a0 30%, #fff 30%, #fff 45%, #ff0000 45%, #ff0000 55%, #000 55%, #000 70%, #0053a0 70%)" },
    Atalanta: { background: "linear-gradient(90deg, #000 50%, #0053a0 50%)" },
    Fiorentina: { background: "#4b0082" },
    Palermo: { background: "linear-gradient(90deg, #f19eb8 50%, #000 50%)" },
    Foggia: { background: "linear-gradient(90deg, #d71920 50%, #111 50%)" },
    Perugia: { background: "linear-gradient(135deg, #d71920 55%, #fff 45%)" },
    Piacenza: { background: "linear-gradient(135deg, #d71920 55%, #fff 45%)" },
    Venezia: { background: "linear-gradient(135deg, #f36c21 0 33%, #111 33% 66%, #007a3d 66%)" },
    Reggina: { background: "linear-gradient(90deg, #8b1731 50%, #111 50%)" },
    Lecce: { background: "linear-gradient(90deg, #e0b21a 50%, #c62828 50%)" },
    Salernitana: { background: "linear-gradient(90deg, #6d1824 55%, #f2d6a2 45%)" },
    Udinese: { background: "linear-gradient(90deg, #000 50%, #fff 50%)" },
    Bologna: { background: "linear-gradient(90deg, #a71b29 50%, #0c2340 50%)" },
    Brescia: { background: "linear-gradient(135deg, #0057a8 55%, #fff 45%)" },
    Cagliari: { background: "linear-gradient(90deg, #a71b29 50%, #0c2340 50%)" },
    Torino: { background: "#7c1f2d" },
    Verona: { background: "linear-gradient(90deg, #005bbb 50%, #ffd200 50%)" },
    Chievo: { background: "linear-gradient(90deg, #ffd200 50%, #005bbb 50%)" },
    Empoli: { background: "#00579c" },
    Vicenza: { background: "linear-gradient(135deg, #e11d2e 55%, #fff 45%)" },
  };
  return styles[club] || { background: "#718096" };
}

export function App() {
  const initialSessionRef = useRef<InitialSession | undefined>(undefined);
  if (!initialSessionRef.current) {
    initialSessionRef.current = loadInitialSession();
  }
  const initialSession = initialSessionRef.current;
  const [mode, setMode] = useState<GameMode>(() => initialSession.mode);
  const [formation, setFormation] = useState<Formation>(() => initialSession.formation);
  const [challengeId, setChallengeId] = useState<DraftChallengeId>(() => initialSession.challengeId);
  const [favoriteClub, setFavoriteClub] = useState<Club>(() => initialSession.favoriteClub);
  const [picks, setPicks] = useState<DraftPick[]>(() => initialSession.picks);
  const [sharedOpponent, setSharedOpponent] = useState<SharedOpponent | undefined>(() => initialSession.sharedOpponent);
  const [result, setResult] = useState<SeasonResult | null>(null);
  const [challengeCopied, setChallengeCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<"campo" | "scelte">("scelte");
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSource, setFeedbackSource] = useState<FeedbackSource>("footer");
  const [feedbackStatus, setFeedbackStatus] = useState<FeedbackStatus>("idle");
  const [feedbackCategory, setFeedbackCategory] = useState("gameplay");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackEmail, setFeedbackEmail] = useState("");
  const [surveyPlatform, setSurveyPlatform] = useState<SurveyPlatform>("ios");
  const [surveyEmail, setSurveyEmail] = useState("");
  const [surveyStatus, setSurveyStatus] = useState<SurveyStatus>("idle");

  const handleTabClick = (tab: "campo" | "scelte") => {
    setActiveTab(tab);
    const targetClass = tab === "campo" ? "lineup-panel" : "draft-panel";
    const element = document.querySelector(`.${targetClass}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const [selectedOfferKey, setSelectedOfferKey] = useState<string | null>(null);
  const [movingSlotId, setMovingSlotId] = useState<string | null>(null);
  const [moveStatus, setMoveStatus] = useState("");
  const [draftSeed, setDraftSeed] = useState(() => initialSession.draftSeed);
  const [packReroll, setPackReroll] = useState(0);
  const [packLoading, setPackLoading] = useState(false);
  const packLoadTimerRef = useRef<number | undefined>(undefined);
  const shareCalloutTrackedRef = useRef(false);
  const sharedChallengeTrackedRef = useRef(false);

  const [lang, setLang] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      const pathLang = window.location.pathname.replace(/^\/+/, "").split("/")[0];
      const normalizedPathLang = pathLang === "pt-br" ? "pt-BR" : pathLanguages.get(pathLang);
      if (normalizedPathLang && pathLang) return normalizedPathLang;

      const urlLang = new URLSearchParams(window.location.search).get("lang");
      if (isLanguage(urlLang)) return urlLang;

      const saved = window.localStorage.getItem("serie-a-38-0-lang");
      if (isLanguage(saved)) return saved;
    }
    return "it";
  });

  const activeLanguageOption = languageOptions.find((option) => option.code === lang) ?? languageOptions[0];

  const changeLang = (nextLang: Language) => {
    setLang(nextLang);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("serie-a-38-0-lang", nextLang);
      const nextPath = languagePaths.get(nextLang) ?? "/";
      const nextUrl = `${window.location.origin}${nextPath}`;
      window.history.replaceState(window.history.state, "", nextUrl);
    }
  };

  const handleLanguageChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextLang = event.target.value;
    if (isLanguage(nextLang)) {
      changeLang(nextLang);
    }
  };

  const lineupSlots = formation.slots;
  const filledSlotIds = new Set(picks.map((pick) => pick.slot.id));
  const openSlots = lineupSlots.filter((slot) => !filledSlotIds.has(slot.id));
  const activeFavoriteClub = mode.requiresClubChoice
    ? mode.clubs.includes(favoriteClub)
      ? favoriteClub
      : mode.clubs[0]
    : undefined;
  const prompt = useMemo(
    () => createPrompt(mode, picks.length, activeFavoriteClub, lang, draftSeed),
    [activeFavoriteClub, draftSeed, mode, picks.length, lang],
  );
  const isTacticalChallenge = challengeId === "tactical";
  const tacticalChallenge = getTacticalChallenge(formation, picks, lang);
  const baseOffers = openSlots.length > 0
    ? getMixedDraftOffers(mode, prompt, picks, activeFavoriteClub, lang, lineupSlots, `${draftSeed}-${packReroll}`)
    : [];
  const offers = isTacticalChallenge
      ? getTacticalSortedOffers(baseOffers, formation, picks)
    : baseOffers;
  const selectedOffer = resolveOfferKey(offers, selectedOfferKey);
  const activeOfferSlotIds = new Set(offers.flatMap((offer) => getSlotOptions(offer).map((slot) => slot.id)));
  const roleMovesEnabled = !mode.locksRoles && !result;
  const movingPick = movingSlotId ? picks.find((pick) => pick.slot.id === movingSlotId) : undefined;
  const moveTargetSlots = movingPick && roleMovesEnabled ? getRoleMoveTargets(movingPick, lineupSlots, picks) : [];
  const moveTargetSlotIds = new Set(moveTargetSlots.map((slot) => slot.id));
  const progress = Math.round((picks.length / lineupSlots.length) * 100);
  const activeModeVariant = getModeVariant(mode.id);
  const activeModeStory = getModeStory(activeModeVariant.storyId);

  useEffect(() => {
    const restored = loadInitialSession();
    setMode(restored.mode);
    setFormation(restored.formation);
    setChallengeId(restored.challengeId);
    setFavoriteClub(restored.favoriteClub);
    setDraftSeed(restored.draftSeed);
    setPicks(restored.picks);
    setSharedOpponent(restored.sharedOpponent);
    setResult(restored.picks.length === restored.formation.slots.length ? simulateSeason(restored.picks, lang, restored.mode) : null);
  }, []);

  useEffect(() => {
    if (picks.length === lineupSlots.length) {
      setResult(simulateSeason(picks, lang, mode));
    }
  }, [lang, lineupSlots.length, mode, picks]);

  useEffect(() => {
    const payload: SavedSession = {
      modeId: mode.id,
      formationId: formation.id,
      challengeId,
      favoriteClub,
      draftSeed,
      pickedPlayerIds: picks.map((pick) => pick.player.id),
      pickedSlotIds: picks.map((pick) => pick.slot.id),
    };
    window.localStorage.setItem(storageKey, JSON.stringify(payload));
  }, [challengeId, draftSeed, favoriteClub, formation.id, mode.id, picks]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const modeName = getTranslatedModeName(mode.id, lang);
    const { title, description } = getRuntimeSeoCopy(lang, modeName);

    document.documentElement.lang = lang;
    document.title = title;
    upsertMeta("name", "description", description);
    const canonicalPath = languagePaths.get(lang) ?? "/";
    const canonicalUrl = `${siteUrl.replace(/\/$/, "")}${canonicalPath}`;
    const alternateLocales = languageOptions
      .filter((option) => option.code !== lang)
      .map((option) => option.locale);

    upsertLink("canonical", canonicalUrl);
    for (const option of languageOptions) {
      upsertAlternate(option.code, `${siteUrl.replace(/\/$/, "")}${option.path}`);
    }
    upsertAlternate("x-default", siteUrl);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:locale", languageLocales.get(lang) ?? "it_IT");
    upsertMetaList("property", "og:locale:alternate", alternateLocales);
    upsertMeta("name", "twitter:url", canonicalUrl);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
  }, [mode.id, lang]);

  useEffect(() => () => {
    if (packLoadTimerRef.current !== undefined) {
      window.clearTimeout(packLoadTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!feedbackOpen || typeof window === "undefined") {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeFeedback();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [feedbackOpen]);

  const draftStarted = picks.length > 0;

  useEffect(() => {
    if (draftStarted || shareCalloutTrackedRef.current) {
      return;
    }

    shareCalloutTrackedRef.current = true;
    trackShareAction("share_callout_view");
  }, [draftStarted]);

  useEffect(() => {
    if (!sharedOpponent || sharedChallengeTrackedRef.current) {
      return;
    }

    sharedChallengeTrackedRef.current = true;
    trackShareAction("shared_challenge_started");
  }, [sharedOpponent]);

  function handleShareCalloutClick() {
    trackShareAction("share_callout_click");
    document.querySelector(".mode-strip")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  function handleCardClick(offerKey: string) {
    if (typeof window !== "undefined" && window.innerWidth <= 992) {
      if (selectedOfferKey !== offerKey) {
        setSelectedOfferKey(offerKey);
        return;
      }
    }
    draft(offerKey);
  }

  function rerollPack() {
    beginPackLoad();
    setPackReroll((value) => value + 1);
    setSelectedOfferKey(null);
    setMovingSlotId(null);
    setMoveStatus("");
  }

  function beginPackLoad(duration = 520) {
    if (typeof window === "undefined") {
      return;
    }

    if (packLoadTimerRef.current !== undefined) {
      window.clearTimeout(packLoadTimerRef.current);
    }
    setPackLoading(true);
    packLoadTimerRef.current = window.setTimeout(() => {
      setPackLoading(false);
      packLoadTimerRef.current = undefined;
    }, duration);
  }

  function chooseMode(nextMode: GameMode) {
    setMode(nextMode);
    trackModeSelected(nextMode);
    if (nextMode.requiresClubChoice && !nextMode.clubs.includes(favoriteClub)) {
      setFavoriteClub(nextMode.clubs[0]);
    }
    setPicks([]);
    setResult(null);
    setChallengeCopied(false);
    setActiveTab("scelte");
    setSelectedOfferKey(null);
    setMovingSlotId(null);
    setMoveStatus("");
    setSharedOpponent(undefined);
    setDraftSeed(makeDraftSeed());
    setPackReroll(0);
    setPackLoading(false);
  }

  function chooseModeStory(storyId: ModeStoryId) {
    const story = getModeStory(storyId);
    chooseMode(getModeForStoryDifficulty(story.id, story.defaultDifficulty));
  }

  function chooseFavoriteClub(club: Club) {
    setFavoriteClub(club);
    trackFavoriteClubSelected(club, mode.id);
    setPicks([]);
    setResult(null);
    setChallengeCopied(false);
    setActiveTab("scelte");
    setSelectedOfferKey(null);
    setMovingSlotId(null);
    setMoveStatus("");
    setSharedOpponent(undefined);
    setDraftSeed(makeDraftSeed());
    setPackReroll(0);
    setPackLoading(false);
  }

  function chooseFormation(nextFormation: Formation) {
    setFormation(nextFormation);
    trackFormationSelected(nextFormation.id, nextFormation.name);
    setPicks([]);
    setResult(null);
    setChallengeCopied(false);
    setActiveTab((prev) => (prev === "campo" ? "campo" : "scelte"));
    setSelectedOfferKey(null);
    setMovingSlotId(null);
    setMoveStatus("");
    setSharedOpponent(undefined);
    setDraftSeed(makeDraftSeed());
    setPackReroll(0);
    beginPackLoad();
  }

  function chooseChallenge(nextChallengeId: DraftChallengeId) {
    if (nextChallengeId === challengeId) {
      return;
    }

    setChallengeId(nextChallengeId);
    setPicks([]);
    setResult(null);
    setChallengeCopied(false);
    setActiveTab("scelte");
    setSelectedOfferKey(null);
    setMovingSlotId(null);
    setMoveStatus("");
    setSharedOpponent(undefined);
    setDraftSeed(makeDraftSeed());
    setPackReroll(0);
    beginPackLoad();
  }

  function draft(offerKey: string) {
    const offer = resolveOfferKey(offers, offerKey);
    if (
      !offer ||
      picks.some((pick) => pick.slot.id === offer.slot.id) ||
      picks.some((pick) => pick.player.id === offer.player.id || normalizePlayerName(pick.player.name) === normalizePlayerName(offer.player.name))
    ) {
      return;
    }

    const nextPicks = [...picks, { slot: offer.slot, player: offer.player }];
    setPicks(nextPicks);
    trackDraftPick(nextPicks[nextPicks.length - 1], nextPicks.length, mode);
    setChallengeCopied(false);
    setSelectedOfferKey(null);
    setMovingSlotId(null);
    setMoveStatus("");
    setPackReroll(0);

    if (nextPicks.length === lineupSlots.length) {
      const nextResult = simulateSeason(nextPicks, lang, mode);
      setResult(nextResult);
      trackDraftCompleted(nextPicks, nextResult, mode);
      setActiveTab("scelte");
    }
  }

  function reset() {
    setPicks([]);
    setResult(null);
    setChallengeCopied(false);
    setActiveTab("scelte");
    setSelectedOfferKey(null);
    setMovingSlotId(null);
    setMoveStatus("");
    if (!sharedOpponent) {
      setDraftSeed(makeDraftSeed());
    }
    setPackReroll(0);
    setPackLoading(false);
  }

  async function copyChallengeLink() {
    try {
      await navigator.clipboard.writeText(createSharedRunUrl({
        mode,
        formation,
        challengeId,
        favoriteClub: activeFavoriteClub,
        draftSeed,
        opponent: result ? createSharedOpponent(picks, result) : undefined,
      }));
      setChallengeCopied(true);
      trackShareAction("copy_challenge");
    } catch {
      setChallengeCopied(false);
    }
  }

  function startRoleMove(slotId: string) {
    if (!roleMovesEnabled) {
      return;
    }

    setSelectedOfferKey(null);
    setMovingSlotId((current) => (current === slotId ? null : slotId));
    setMoveStatus("");
  }

  function movePickToSlot(targetSlotId: string) {
    if (!roleMovesEnabled || !movingPick) {
      return;
    }

    const targetSlot = getRoleMoveTargets(movingPick, lineupSlots, picks).find((slot) => slot.id === targetSlotId);
    if (!targetSlot) {
      return;
    }

    const sourceLabel = tSlotLabel(movingPick.slot, lang);
    const targetLabel = tSlotLabel(targetSlot, lang);
    const nextPicks = picks.map((pick) =>
      pick.slot.id === movingPick.slot.id
        ? { ...pick, slot: targetSlot }
        : pick,
    );

    setPicks(nextPicks);
    setChallengeCopied(false);
    setSelectedOfferKey(null);
    setMovingSlotId(null);
    setMoveStatus(t.roleMoveToast(sourceLabel, targetLabel));
    setActiveTab("campo");
  }

  function openFeedback(source: FeedbackSource) {
    setFeedbackOpen(true);
    setFeedbackSource(source);
    setFeedbackStatus("idle");
    trackFeedbackOpened(source);
  }

  function closeFeedback() {
    setFeedbackOpen(false);
    setFeedbackStatus("idle");
  }

  async function submitFeedback(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!feedbackMessage.trim()) {
      setFeedbackStatus("error");
      return;
    }

    setFeedbackStatus("submitting");
    const context = getFeedbackContext({
      mode,
      formation,
      favoriteClub: activeFavoriteClub,
      picks,
      result,
      lang,
      source: feedbackSource,
      challengeId,
    });

    const payload = new URLSearchParams({
      "form-name": "site-feedback",
      category: feedbackCategory,
      message: feedbackMessage.trim(),
      email: feedbackEmail.trim(),
      language: lang,
      mode: mode.id,
      formation: formation.id,
      challenge: challengeId,
      progress: `${picks.length}/11`,
      source: feedbackSource,
      context,
      path: typeof window !== "undefined" ? window.location.pathname : "/",
    });

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      if (!response.ok) {
        throw new Error(`Feedback submission failed with ${response.status}`);
      }

      setFeedbackStatus("sent");
      setFeedbackMessage("");
      setFeedbackEmail("");
      trackFeedbackSubmitted(feedbackCategory);
    } catch (error) {
      console.error(error);
      setFeedbackStatus("error");
    }
  }

  async function submitAppInterest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSurveyStatus("submitting");

    const payload = new URLSearchParams({
      "form-name": "app-interest",
      platform: surveyPlatform,
      email: surveyEmail.trim(),
      language: lang,
      mode: mode.id,
      formation: formation.id,
      challenge: challengeId,
      progress: `${picks.length}/11`,
      path: typeof window !== "undefined" ? window.location.pathname : "/",
    });

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      if (!response.ok) {
        throw new Error(`App interest submission failed with ${response.status}`);
      }

      setSurveyStatus("sent");
      setSurveyEmail("");
      trackAppInterestSubmitted(surveyPlatform, Boolean(surveyEmail.trim()));
    } catch (error) {
      console.error(error);
      setSurveyStatus("error");
    }
  }

  const t = translations[lang] as any;
  const playerDisplayName = createPlayerDisplayNameResolver(picks.map((pick) => pick.player));
  const concealOfferDetails = isHiddenOfferMode(mode.id);
  const concealLineupDetails = concealOfferDetails && !result;
  const appInterestSurvey = (
    <AppInterestSurvey
      email={surveyEmail}
      lang={lang}
      onEmailChange={(value) => {
        setSurveyEmail(value);
        if (surveyStatus === "error") {
          setSurveyStatus("idle");
        }
      }}
      onPlatformChange={(value) => {
        setSurveyPlatform(value);
        if (surveyStatus === "error") {
          setSurveyStatus("idle");
        }
      }}
      onSubmit={submitAppInterest}
      platform={surveyPlatform}
      status={surveyStatus}
    />
  );

  return (
    <>
    <main className="app-shell">
      <header className={`hero ${draftStarted ? "collapsed-mobile" : ""}`}>
        <div className="hero-copy">
          <p className="eyebrow">{t.heroEyebrow}</p>
          <h1>{t.heroTitle}</h1>
          <p className="subtitle">{t.heroSubtitle}</p>
          {!draftStarted && (
            <button className="hero-share-callout" type="button" onClick={handleShareCalloutClick} aria-label={t.shareCalloutLabel}>
              <Share2 size={18} />
              <div>
                <strong>{t.shareCalloutTitle}</strong>
                <span>{t.shareCalloutBody}</span>
              </div>
            </button>
          )}
        </div>
        <div className="hero-actions">
          <label className="lang-selector" htmlFor="language-select">
            <span className="lang-selector-label">{t.languageSelectLabel}</span>
            <select
              id="language-select"
              className="lang-select"
              value={lang}
              onChange={handleLanguageChange}
              aria-label={t.languageSelectLabel}
            >
              {languageOptions.map((option) => (
                <option key={option.code} value={option.code} lang={option.code}>
                  {option.flag} {option.nativeName}
                </option>
              ))}
            </select>
            <span className="lang-selector-current" aria-hidden="true">{activeLanguageOption.label}</span>
          </label>
          <div className="score-tile" aria-label={t.draftProgress}>
            <span>{picks.length}/11</span>
            <strong>{result ? t.simulated : t.draftXi}</strong>
          </div>
        </div>
      </header>

      <section className={`mode-strip ${draftStarted ? "hidden-mobile" : ""}`} aria-label={t.modeSelectorLabel}>
        <div className="story-selector" role="group" aria-label={t.storySelectorLabel}>
          {modeStories.map((story) => {
            const storyInfo = getModeStoryInfo(story.id, lang);
            const activeDifficulty = getDifficultyInfo(
              story.id === activeModeStory.id ? activeModeVariant.difficulty : story.defaultDifficulty,
              lang,
            );
            const isActive = story.id === activeModeStory.id;

            return (
              <div className={isActive ? "story-card active" : "story-card"} key={story.id}>
                <button
                  aria-pressed={isActive}
                  className="story-button"
                  onClick={() => chooseModeStory(story.id)}
                  type="button"
                >
                  <span className="mode-title-row">
                    <span>{storyInfo.name}</span>
                    <em className={`difficulty-badge difficulty-${activeDifficulty.level}`}>{activeDifficulty.label}</em>
                  </span>
                  <small>{storyInfo.desc}</small>
                </button>
                <div className="story-difficulty-options" role="group" aria-label={`${storyInfo.name} ${t.difficultySelectorLabel}`}>
                  {story.difficulties.map((difficultyId) => {
                    const variantMode = getModeForStoryDifficulty(story.id, difficultyId);
                    const difficulty = getDifficultyInfo(difficultyId, lang);
                    const isDifficultyActive = variantMode.id === mode.id;

                    return (
                      <button
                        aria-pressed={isDifficultyActive}
                        className={isDifficultyActive ? "story-difficulty-button active" : "story-difficulty-button"}
                        key={difficultyId}
                        onClick={() => chooseMode(variantMode)}
                        type="button"
                      >
                        <span>{difficulty.label}</span>
                        {variantMode.locksRoles && <em>{t.fixedRolesLabel}</em>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className={`challenge-strip ${draftStarted ? "compact" : ""}`} aria-label={t.advancedChallengeLabel}>
        <div className="challenge-copy">
          <span>{t.advancedChallengeEyebrow}</span>
          <strong>{isTacticalChallenge ? t.challengeTacticalName : t.challengeClassicName}</strong>
          <small>{isTacticalChallenge ? t.challengeTacticalDesc : t.challengeClassicDesc}</small>
        </div>
        <div className="challenge-options" role="group" aria-label={t.advancedChallengeLabel}>
          <button
            aria-pressed={challengeId === "standard"}
            className={challengeId === "standard" ? "challenge-button active" : "challenge-button"}
            onClick={() => chooseChallenge("standard")}
            type="button"
          >
            <Shield size={16} />
            <span>{t.challengeClassicName}</span>
          </button>
          <button
            aria-pressed={challengeId === "tactical"}
            className={challengeId === "tactical" ? "challenge-button active" : "challenge-button"}
            onClick={() => chooseChallenge("tactical")}
            type="button"
          >
            <Sparkles size={16} />
            <span>{t.challengeTacticalName}</span>
          </button>
        </div>
      </section>

      {/* Mobile Tab Navigation */}
      <div className="mobile-tabs-nav">
        <button
          className={`tab-nav-button ${activeTab === "scelte" ? "active" : ""}`}
          onClick={() => handleTabClick("scelte")}
          type="button"
        >
          {result ? t.risultati : `${t.draftTabLabel} (${picks.length}/11)`}
        </button>
        <button
          className={`tab-nav-button ${activeTab === "campo" ? "active" : ""}`}
          onClick={() => handleTabClick("campo")}
          type="button"
        >
          {t.laTuaFormazione}
        </button>
      </div>

      <section className={`game-layout ${result ? "results-phase" : "drafting-phase"} ${draftStarted ? "draft-started" : "draft-not-started"}`}>
        {/* IL CAMPO TATTICO */}
        <aside className={`lineup-panel ${activeTab === "campo" ? "mobile-show" : "mobile-hide"} ${result ? "results-active" : "drafting-active"} ${draftStarted ? "draft-started" : "draft-not-started"}`} aria-label={t.lineupPanelLabel}>
          <div className="lineup-heading">
            <div>
              <p className="eyebrow">{t.moduloTattico}</p>
              <h2>{formation.name}</h2>
            </div>
            <div className="chemistry-indicator">
              <Sparkles size={16} />
              <span>{t.intesa}: {concealLineupDetails ? "??" : `+${result ? result.chemistry : calculateCurrentChemistry(picks)}`}</span>
            </div>
          </div>
          {!result && (
            <FormationSelector formation={formation} onSelect={chooseFormation} lang={lang} />
          )}

          <div className="pitch">
            {/* Campo da gioco SVG markings */}
            <svg className="pitch-markings" viewBox="0 0 100 130" preserveAspectRatio="none">
              <rect x="4" y="4" width="92" height="122" fill="none" stroke="rgba(255, 255, 255, 0.08)" strokeWidth="1"/>
              <line x1="4" y1="65" x2="96" y2="65" stroke="rgba(255, 255, 255, 0.08)" strokeWidth="1"/>
              <circle cx="50" cy="65" r="16" fill="none" stroke="rgba(255, 255, 255, 0.08)" strokeWidth="1"/>
              <circle cx="50" cy="65" r="1" fill="rgba(255, 255, 255, 0.15)"/>
              <rect x="24" y="4" width="52" height="22" fill="none" stroke="rgba(255, 255, 255, 0.08)" strokeWidth="1"/>
              <rect x="36" y="4" width="28" height="8" fill="none" stroke="rgba(255, 255, 255, 0.08)" strokeWidth="1"/>
              <rect x="24" y="104" width="52" height="22" fill="none" stroke="rgba(255, 255, 255, 0.08)" strokeWidth="1"/>
              <rect x="36" y="118" width="28" height="8" fill="none" stroke="rgba(255, 255, 255, 0.08)" strokeWidth="1"/>
              <path d="M 36 26 A 16 16 0 0 0 64 26" fill="none" stroke="rgba(255, 255, 255, 0.08)" strokeWidth="1"/>
              <path d="M 36 104 A 16 16 0 0 1 64 104" fill="none" stroke="rgba(255, 255, 255, 0.08)" strokeWidth="1"/>
            </svg>

            {/* Linee di intesa SVG */}
            <svg className="pitch-chemistry-lines" viewBox="0 0 100 100" preserveAspectRatio="none">
              {(CHEMISTRY_LINKS_BY_FORMATION[formation.id] ?? chemistryLinks433).map((link, idx) => {
                const fromCoord = SLOT_COORDS[link.from];
                const toCoord = SLOT_COORDS[link.to];
                if (!fromCoord || !toCoord) return null;

                const pickFrom = picks.find((p) => p.slot.id === link.from);
                const pickTo = picks.find((p) => p.slot.id === link.to);

                let stroke = "rgba(255, 255, 255, 0.12)";
                let strokeWidth = "1.5";
                let filter = "";
                let isConnected = false;

                if (pickFrom && pickTo && !concealLineupDetails) {
                  isConnected = true;
                  const sameClub = pickFrom.player.club === pickTo.player.club;
                  const sameCountry = pickFrom.player.country === pickTo.player.country;
                  const sameEra = pickFrom.player.era === pickTo.player.era;

                  if (sameClub) {
                    stroke = "#10b981"; // Neon Green
                    strokeWidth = "3";
                    filter = "drop-shadow(0px 0px 4px rgba(16, 185, 129, 0.8))";
                  } else if (sameCountry) {
                    stroke = "#3b82f6"; // Neon Blue
                    strokeWidth = "3";
                    filter = "drop-shadow(0px 0px 4px rgba(59, 130, 246, 0.8))";
                  } else if (sameEra) {
                    stroke = "#f59e0b"; // Neon Amber/Gold
                    strokeWidth = "3";
                    filter = "drop-shadow(0px 0px 4px rgba(245, 158, 11, 0.8))";
                  } else {
                    stroke = "#64748b"; // Basic line
                    strokeWidth = "2";
                  }
                } else if (pickFrom && pickTo) {
                  isConnected = true;
                  stroke = "rgba(148, 163, 184, 0.24)";
                  strokeWidth = "2";
                }

                return (
                  <line
                    key={idx}
                    x1={fromCoord.x}
                    y1={fromCoord.y}
                    x2={toCoord.x}
                    y2={toCoord.y}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeDasharray={isConnected ? undefined : "2 2"}
                    style={{ filter }}
                    className="chemistry-line"
                  />
                );
              })}
            </svg>

            {/* Render slots */}
            {lineupSlots.map((slot) => {
              const pick = picks.find((item) => item.slot.id === slot.id);
              const coords = SLOT_COORDS[slot.id];
              const isSlotActive = !result && activeOfferSlotIds.has(slot.id);
              const isSelectedTarget = selectedOffer?.slot.id === slot.id;
              const isMoveSource = movingPick?.slot.id === slot.id;
              const isMoveTarget = moveTargetSlotIds.has(slot.id);
              const isMoveMuted = Boolean(movingPick && !pick && !isMoveTarget);
              const slotLabel = t[slot.id] || slot.label;
              const shortName = pick ? playerDisplayName(pick.player.name) : "";
              const nameClass = pick
                ? `slot-player-name ${shortName.length > 10 ? "font-xs" : shortName.length > 8 ? "font-sm" : ""}`
                : "";

              return (
                <div
                  className={`slot ${pick ? "filled" : ""} ${isSlotActive ? "active" : ""} ${isSelectedTarget ? "targeted" : ""} ${isMoveSource ? "move-source" : ""} ${isMoveTarget ? "move-target" : ""} ${isMoveMuted ? "move-muted" : ""} ${pick && !concealLineupDetails ? `rarity-${getRarityClass(pick.player)}` : ""} ${concealLineupDetails && pick ? "values-hidden" : ""}`}
                  style={{ top: `${coords.y}%`, left: `${coords.x}%` }}
                  key={slot.id}
                  data-slot-id={slot.id}
                >
                  <div className="slot-glow" />
                  <span className="slot-label">{slotLabel}</span>
                  
                  {pick ? (
                    <div
                      className={roleMovesEnabled ? "slot-fut-inner role-movable" : "slot-fut-inner role-locked"}
                      onClick={() => startRoleMove(slot.id)}
                      title={roleMovesEnabled ? t.roleMoveLabel : t.fixedRolesLabel}
                    >
                      <button
                        aria-label={roleMovesEnabled ? t.roleMoveAria(pick.player.name) : t.fixedRolesAria(pick.player.name)}
                        className={`slot-move-button ${roleMovesEnabled ? "" : "locked"} ${isMoveSource ? "active" : ""}`}
                        disabled={!roleMovesEnabled}
                        onClick={(event) => {
                          event.stopPropagation();
                          startRoleMove(slot.id);
                        }}
                        title={roleMovesEnabled ? t.roleMoveLabel : t.fixedRolesLabel}
                        type="button"
                      >
                        {roleMovesEnabled ? <ArrowRightLeft size={13} /> : <Lock size={13} />}
                      </button>
                      <div className="slot-fut-header">
                        <span className="slot-rating">{concealLineupDetails ? "??" : pick.player.score}</span>
                        <span className="slot-flag">{concealLineupDetails ? "?" : getCountryEmoji(pick.player.country)}</span>
                      </div>
                      <strong className={nameClass}>{shortName}</strong>
                      <div className="slot-fut-footer">
                        <div
                          className={concealLineupDetails ? "club-colors-crest hidden-crest" : "club-colors-crest"}
                          style={concealLineupDetails ? undefined : getClubBadgeStyle(pick.player.club)}
                          title={concealLineupDetails ? t.hiddenSourceLabel : pick.player.club}
                        />
                        <small className="slot-club-season">
                          {concealLineupDetails ? t.hiddenMiniStatsLabel : `${pick.player.club}, ${pick.player.season}`}
                        </small>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="slot-placeholder">
                        <div className="plus-icon">{isSlotActive ? "✦" : "+"}</div>
                        <strong style={{ display: "none" }}>{t.daScegliere}</strong>
                        <small className="slot-role">{slotLabel}</small>
                      </div>
                      {isMoveTarget && (
                        <>
                          <button
                            aria-label={t.moveToSlot(slotLabel)}
                            className="move-target-button"
                            data-target-slot={slot.id}
                            onClick={() => movePickToSlot(slot.id)}
                            title={t.moveToSlot(slotLabel)}
                            type="button"
                          />
                          <span className="move-target-label">{t.roleMoveLabel}</span>
                        </>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {movingPick && !result && (
            <section className="role-move-panel" aria-label={t.roleMovePanelTitle} role="region">
              <div className="role-move-panel-heading">
                <div>
                  <span>{t.roleMoveLabel}</span>
                  <strong>{movingPick.player.name}</strong>
                </div>
                <button className="role-move-close" type="button" onClick={() => setMovingSlotId(null)} aria-label={t.closeRoleMovePanel} title={t.closeRoleMovePanel}>
                  <X size={16} />
                </button>
              </div>
              <div className="role-move-current">
                <span>{t.currentRoleLabel}</span>
                <strong>{tSlotLabel(movingPick.slot, lang)}</strong>
              </div>
              <p>{t.roleMovePanelBody}</p>
              {moveTargetSlots.length > 0 ? (
                <div className="role-move-options" aria-label={t.availableRolesLabel}>
                  {moveTargetSlots.map((slot) => {
                    const slotLabel = tSlotLabel(slot, lang);
                    return (
                      <button
                        className="role-move-choice"
                        data-target-slot={slot.id}
                        key={slot.id}
                        onClick={() => movePickToSlot(slot.id)}
                        title={t.moveToSlot(slotLabel)}
                        type="button"
                      >
                        <span>{t[`${slot.id}_abbr`] || slotLabel}</span>
                        <small>{t.moveToSlot(slotLabel)}</small>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="role-move-empty">{t.noRoleMovesAvailable}</p>
              )}
            </section>
          )}

          <p className="move-status" aria-live="polite">{moveStatus}</p>

        </aside>

        {/* PACCHETTI E SCELTE / RISULTATO */}
        <div className={`draft-panel ${activeTab === "scelte" ? "mobile-show" : "mobile-hide"} ${result ? "results-active" : "drafting-active"} ${draftStarted ? "draft-started" : "draft-not-started"}`}>
          {!result && <div className="drawer-handle" />}
          <div className="panel-heading">
            <div>
              <p className="eyebrow">{getTranslatedModeName(mode.id, lang) || mode.name}</p>
              <h2>{result ? t.risultatoStagione : t.roundLabel(picks.length + 1)}</h2>
            </div>
            <div className="panel-actions">
              {!result && offers.length > 0 && (
                <button className="icon-button reroll" type="button" onClick={rerollPack} aria-label={t.rimescolaPack} title={t.rimescolaPack}>
                  <Shuffle size={18} />
                </button>
              )}
              {!result && (
                <button className="icon-button reset" type="button" onClick={reset} aria-label={t.ricominciaDraft} title={t.ricominciaDraft}>
                  <RotateCcw size={18} />
                </button>
              )}
            </div>
          </div>

          <div className="progress-track" aria-label={t.draftProgressStatus(progress)}>
            <span style={{ width: `${progress}%` }} />
          </div>

          {sharedOpponent && !result && (
            <SharedChallengeBanner
              lang={lang}
              opponent={sharedOpponent}
              pickedCount={picks.length}
              totalSlots={lineupSlots.length}
              progress={progress}
            />
          )}

          {isTacticalChallenge && (
            <div className={tacticalChallenge.complete ? "tactical-status complete" : "tactical-status"} aria-label={t.tacticalStatusLabel}>
              <div>
                <span>{t.challengeTacticalName}</span>
                <strong>{tacticalChallenge.title}</strong>
              </div>
              <p>{tacticalChallenge.body}</p>
              <div className="tactical-meter" aria-hidden="true">
                <span style={{ width: `${clamp((tacticalChallenge.current / tacticalChallenge.target) * 100, 0, 100)}%` }} />
              </div>
              <small>
                {tacticalChallenge.complete ? t.tacticalCompleteLabel : t.tacticalOpenLabel}: {tacticalChallenge.progress}
              </small>
            </div>
          )}

          {mode.requiresClubChoice && activeFavoriteClub && !result && (
            <ClubSelector favoriteClub={activeFavoriteClub} mode={mode} onSelect={chooseFavoriteClub} lang={lang} />
          )}

          {result ? (
            <>
              <ResultCard
                challengeCopied={challengeCopied}
                onCopyChallenge={copyChallengeLink}
                onFeedback={() => openFeedback("result")}
                onReset={reset}
                result={result}
                picks={picks}
                formation={formation}
                mode={mode}
                tacticalChallenge={isTacticalChallenge ? tacticalChallenge : undefined}
                sharedOpponent={sharedOpponent}
                lang={lang}
              />
              {appInterestSurvey}
            </>
          ) : (
            <>
              <div className={`candidate-grid ${packLoading ? "loading" : ""}`} aria-label={t.giocatoriDisponibili}>
                {offers.map((offer) => {
                  const stats = getCardStats(offer.player);
                  const isIcon = offer.rarity === "icona";
                  const offerKey = getOfferKey(offer);
                  const offerSlotLabel = t[offer.slot.id] || offer.slot.label;
                  const offerSlotLabelAbbr = t[`${offer.slot.id}_abbr`] || offerSlotLabel;
                  const slotOptions = getSlotOptions(offer);
                  const hasSlotChoice = slotOptions.length > 1;
                  const isTacticalFit = isTacticalChallenge && getTacticalFitSlots(offer, formation, picks).length > 0;
                  const offerReason = concealOfferDetails ? t.hiddenReasonLabel : offer.reason;
                  const offerLane = concealOfferDetails ? t.hiddenLaneLabel : offer.lane;
                  const offerRarityLabel = concealOfferDetails ? t.hiddenRarityLabel : (t[offer.rarity] || offer.rarity);
                  const clubSeasonLabel = concealOfferDetails ? t.hiddenMiniStatsLabel : `${offer.player.club.toUpperCase()} • ${offer.player.season}`;
                  const sourceLabel = concealOfferDetails
                    ? t.hiddenSourceLabel
                    : offer.player.source
                      ? `${t.sourced}: ${offer.player.source.name}`
                      : offer.player.evidence?.[0]
                        ? `${t.sourced}: ${offer.player.evidence[0].name}`
                        : (t[offer.dataLevel] ?? t.profiloCuratoDefault);

                  return (
                    <article
                      aria-label={`${offer.player.name}, ${t.slotTarget}: ${offerSlotLabel}`}
                      aria-selected={selectedOfferKey === offerKey}
                      className={`player-card rarity-${concealOfferDetails ? "hidden" : offer.rarity} ${concealOfferDetails ? "values-hidden" : ""} ${selectedOfferKey === offerKey ? "selected" : ""}`}
                      data-offer-player={offer.player.id}
                      data-offer-slot={offer.slot.id}
                      data-tactical-fit={concealOfferDetails ? "hidden" : isTacticalFit ? "true" : "false"}
                      key={offerKey}
                      onClick={() => handleCardClick(offerKey)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleCardClick(offerKey);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="card-shine" />
                      <div className="card-glow" />
                      {selectedOfferKey === offerKey && (
                        <div className="card-confirm-overlay">
                          <span className="confirm-text">
                            {t.signCard}
                          </span>
                        </div>
                      )}
                      
                      {/* FUT Card Layout */}
                      <div className="card-fut-top">
                        <div className="card-badge-container">
                          <span
                            className="player-score"
                            aria-label={concealOfferDetails ? t.hiddenCardValueLabel : `${t.gameplayRatingLabel} ${offer.player.score}`}
                          >
                            {concealOfferDetails ? "??" : offer.player.score}
                          </span>
                          <span className="card-position">{offerSlotLabelAbbr}</span>
                          <span className="card-flag" title={concealOfferDetails ? t.hiddenSourceLabel : offer.player.country}>
                            {concealOfferDetails ? "?" : getCountryEmoji(offer.player.country)}
                          </span>
                          <div
                            className={concealOfferDetails ? "club-colors-crest hidden-crest" : "club-colors-crest"}
                            style={concealOfferDetails ? undefined : getClubBadgeStyle(offer.player.club)}
                            title={concealOfferDetails ? t.hiddenSourceLabel : offer.player.club}
                          />
                        </div>
                        {isIcon && !concealOfferDetails && <Star className="icon-badge-star" size={14} fill="#f1d36b" color="#f1d36b" />}
                      </div>

                      <div className="card-fut-info">
                        <strong>{offer.player.name}</strong>
                        <span className="card-club-season-badge">
                          {clubSeasonLabel}
                        </span>
                      </div>

                      {/* FUT calculated stats grid */}
                      <div className="card-stats-grid">
                        {stats.map((s) => (
                          <div className="card-stat-item" key={s.label}>
                            <span className="stat-value" aria-label={concealOfferDetails ? t.hiddenStatValueLabel : undefined}>
                              {concealOfferDetails ? "??" : s.value}
                            </span>
                            <span className="stat-label">{s.label}</span>
                          </div>
                        ))}
                      </div>

                      {!concealOfferDetails && <div className="card-reason-banner">
                        <span className="fit-badge">{offerReason}</span>
                      </div>}

                      {hasSlotChoice && !concealOfferDetails && (
                        <div className="slot-choice-row" aria-label={t.scegliSlotCarta}>
                          {slotOptions.map((slot) => {
                            const slotKey = getOfferKeyForSlot(offer, slot);
                            const slotLabel = t[slot.id] || slot.label;
                            const slotAbbr = t[`${slot.id}_abbr`] || slotLabel;
                            return (
                              <button
                                aria-pressed={slot.id === offer.slot.id}
                                className={slot.id === offer.slot.id ? "slot-choice active" : "slot-choice"}
                                key={slot.id}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  draft(slotKey);
                                }}
                                title={`${t.slotTarget}: ${slotLabel}`}
                                type="button"
                              >
                                {slotAbbr}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <div className="card-metadata-extra">
                        <span className="target-slot-note">{t.slotTarget}: {offerSlotLabel}</span>
                        {!concealOfferDetails && <span className="pack-lane">{offerLane}</span>}
                        {isTacticalFit && !concealOfferDetails && <span className="tactical-pill">{t.tacticalFitLabel}</span>}
                        {!concealOfferDetails && (
                          <span className="rating-note">
                            {offerRarityLabel} · {t.intesa} {offer.chemistry}
                          </span>
                        )}
                        {!concealOfferDetails && <span className="profile-tags">{offer.profileTags?.join(" · ")}</span>}
                        {!concealOfferDetails && <small>{offer.player.tag} · {offer.player.country}</small>}
                        {!concealOfferDetails && <em>{offer.player.note}</em>}
                        <span className="mini-stats">
                          {concealOfferDetails
                            ? t.hiddenMiniStatsLabel
                            : (
                              <>
                                {offer.player.goals ? `${offer.player.goals} ${t.golStats}` : t.ruoloChiave}
                                {offer.player.assists ? ` · ${offer.player.assists} ${t.assistStats}` : ""}
                                {offer.player.appearances ? ` · ${offer.player.appearances} ${t.presStats}` : ""}
                              </>
                            )}
                        </span>
                      </div>

                      {!concealOfferDetails && <span className={(offer.player.source || offer.player.evidence?.length) ? "source-pill sourced" : "source-pill"}>
                        {sourceLabel}
                      </span>}
                    </article>
                  );
                })}
              </div>
            </>
          )}

          <div className="mobile-roster-list">
            <h3>{t.draftedPlayersTitle}</h3>
            {picks.length === 0 ? (
              <p className="empty-roster-note">{t.emptyRosterNote}</p>
            ) : (
              <div className="roster-list-grid">
                {picks.map((pick) => {
                  const positionAbbr = (translations[lang] as any)[`${pick.slot.id}_abbr`] || pick.slot.id.toUpperCase();
                  const rarityClass = concealLineupDetails ? "hidden" : getRarityClass(pick.player);
                  return (
                    <div className={`roster-list-item rarity-${rarityClass} ${concealLineupDetails ? "values-hidden" : ""} ${movingSlotId === pick.slot.id ? "move-source" : ""}`} key={pick.slot.id}>
                      <span className={`roster-item-pos role-${pick.slot.role.toLowerCase()}`}>{positionAbbr}</span>
                      <div className="roster-item-info">
                        <div className="roster-item-name-row">
                          <strong>{pick.player.name}</strong>
                          <span className="roster-item-flag" title={concealLineupDetails ? t.hiddenSourceLabel : pick.player.country}>
                            {concealLineupDetails ? "?" : getCountryEmoji(pick.player.country)}
                          </span>
                        </div>
                        <div className="roster-item-club-row">
                          <div
                            className={concealLineupDetails ? "club-colors-crest hidden-crest" : "club-colors-crest"}
                            style={concealLineupDetails ? undefined : getClubBadgeStyle(pick.player.club)}
                            title={concealLineupDetails ? t.hiddenSourceLabel : pick.player.club}
                          />
                          <span className="roster-item-club">
                            {concealLineupDetails ? t.hiddenMiniStatsLabel : pick.player.club.toUpperCase()}
                          </span>
                          {!concealLineupDetails && <span className="roster-item-dot">•</span>}
                          {!concealLineupDetails && <span className="roster-item-season">{pick.player.season}</span>}
                        </div>
                      </div>
                      <div className="roster-item-score-badge">
                        <span className="roster-item-score">{concealLineupDetails ? "??" : pick.player.score}</span>
                      </div>
                      <button
                        aria-label={roleMovesEnabled ? t.roleMoveAria(pick.player.name) : t.fixedRolesAria(pick.player.name)}
                        className={`roster-move-button ${roleMovesEnabled ? "" : "locked"}`}
                        disabled={!roleMovesEnabled}
                        onClick={() => startRoleMove(pick.slot.id)}
                        title={roleMovesEnabled ? t.roleMoveLabel : t.fixedRolesLabel}
                        type="button"
                      >
                        {roleMovesEnabled ? <ArrowRightLeft size={14} /> : <Lock size={14} />}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {!draftStarted && (
        <section className="seo-discovery" aria-labelledby="seo-discovery-title">
          <div>
            <p className="eyebrow">{t.seoEyebrow}</p>
            <h2 id="seo-discovery-title">{t.seoTitle}</h2>
            <p>{t.seoBody}</p>
          </div>
          <div className="seo-instructions" aria-label={t.seoInstructionsLabel}>
            {[
              { title: t.seoStepModeTitle, body: t.seoStepModeBody },
              { title: t.seoStepDraftTitle, body: t.seoStepDraftBody },
              { title: t.seoStepSeasonTitle, body: t.seoStepSeasonBody },
            ].map((step) => (
              <details className="seo-step" key={step.title}>
                <summary>
                  <strong>{step.title}</strong>
                  <span>{t.seoReadMore}</span>
                </summary>
                <p>{step.body}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {!result && appInterestSurvey}

      <div className="notice">
        <div className="notice-header">
          <Info size={14} />
          <strong>{t.origineDati}</strong>
        </div>
        <p>{t.dataNotice}</p>
        <button className="feedback-link" type="button" onClick={() => openFeedback("footer")}>
          <MessageSquare size={15} />
          <span>{t.feedbackOpen}</span>
        </button>
      </div>

    </main>
    <CookieConsentBanner elevated={draftStarted} lang={lang} />
    {feedbackOpen && (
      <FeedbackDialog
        category={feedbackCategory}
        email={feedbackEmail}
        lang={lang}
        message={feedbackMessage}
        onCategoryChange={setFeedbackCategory}
        onClose={closeFeedback}
        onEmailChange={setFeedbackEmail}
        onMessageChange={(value) => {
          setFeedbackMessage(value);
          if (feedbackStatus === "error") {
            setFeedbackStatus("idle");
          }
        }}
        onSubmit={submitFeedback}
        status={feedbackStatus}
      />
    )}
    </>
  );
}

function AppInterestSurvey({
  email,
  lang,
  onEmailChange,
  onPlatformChange,
  onSubmit,
  platform,
  status,
}: {
  email: string;
  lang: Language;
  onEmailChange: (email: string) => void;
  onPlatformChange: (platform: SurveyPlatform) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  platform: SurveyPlatform;
  status: SurveyStatus;
}) {
  const t = translations[lang] as any;
  const options: Array<{ value: SurveyPlatform; label: string }> = [
    { value: "ios", label: t.appSurveyPlatformIos },
    { value: "android", label: t.appSurveyPlatformAndroid },
    { value: "both", label: t.appSurveyPlatformBoth },
  ];

  return (
    <section className="app-survey" aria-labelledby="app-survey-title">
      <div className="app-survey-copy">
        <p className="eyebrow">{t.appSurveyEyebrow}</p>
        <h2 id="app-survey-title">
          <Smartphone size={18} />
          <span>{t.appSurveyTitle}</span>
        </h2>
        <p>{t.appSurveyBody}</p>
      </div>

      <form className="app-survey-form" name="app-interest" method="POST" data-netlify="true" onSubmit={onSubmit}>
        <input type="hidden" name="form-name" value="app-interest" />
        <div className="app-survey-options" role="radiogroup" aria-label={t.appSurveyPlatformLabel}>
          {options.map((option) => (
            <label className={platform === option.value ? "app-survey-option active" : "app-survey-option"} key={option.value}>
              <input
                checked={platform === option.value}
                name="platform"
                onChange={() => onPlatformChange(option.value)}
                type="radio"
                value={option.value}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>

        <label className="app-survey-email">
          <span>{t.appSurveyEmailLabel}</span>
          <input
            name="email"
            type="email"
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
            placeholder={t.appSurveyEmailPlaceholder}
          />
        </label>

        <div className="app-survey-actions">
          <button className="app-survey-submit" type="submit" disabled={status === "submitting" || status === "sent"}>
            {status === "submitting" ? <Sparkles size={16} className="animate-spin" /> : <Send size={16} />}
            <span>{status === "submitting" ? t.appSurveySending : status === "sent" ? t.appSurveySentButton : t.appSurveySubmit}</span>
          </button>
          <small>{t.appSurveyPrivacy}</small>
        </div>

        {status === "sent" && <p className="feedback-status success">{t.appSurveySuccess}</p>}
        {status === "error" && <p className="feedback-status error">{t.appSurveyError}</p>}
      </form>
    </section>
  );
}

function FormationSelector({
  formation,
  onSelect,
  lang,
}: {
  formation: Formation;
  onSelect: (formation: Formation) => void;
  lang: Language;
}) {
  const t = translations[lang] as any;

  return (
    <div className="formation-selector" aria-label={t.scegliModulo}>
      {formations.map((item) => (
        <button
          aria-pressed={item.id === formation.id}
          className={item.id === formation.id ? "formation-chip active" : "formation-chip"}
          key={item.id}
          onClick={() => onSelect(item)}
          title={item.description}
          type="button"
        >
          {item.shortName}
        </button>
      ))}
    </div>
  );
}

function ClubSelector({
  favoriteClub,
  mode,
  onSelect,
  lang,
}: {
  favoriteClub: Club;
  mode: GameMode;
  onSelect: (club: Club) => void;
  lang: Language;
}) {
  const t = translations[lang] as any;
  return (
    <div className="club-selector" aria-label={t.scegliClubFocus}>
      <div className="club-selector-info">
        <span className="prompt-kicker">{t.clubFocusKicker}</span>
        <strong>{favoriteClub}</strong>
      </div>
      <div className="club-options">
        {mode.clubs.map((club) => (
          <button
            aria-pressed={club === favoriteClub}
            className={club === favoriteClub ? "club-chip active" : "club-chip"}
            key={club}
            onClick={() => onSelect(club)}
            type="button"
          >
            {club}
          </button>
        ))}
      </div>
    </div>
  );
}

function ResultCard({
  challengeCopied,
  onCopyChallenge,
  onFeedback,
  onReset,
  result,
  picks,
  formation,
  mode,
  tacticalChallenge,
  sharedOpponent,
  lang,
}: {
  challengeCopied: boolean;
  onCopyChallenge: () => void;
  onFeedback: () => void;
  onReset: () => void;
  result: SeasonResult;
  picks: DraftPick[];
  formation: Formation;
  mode: GameMode;
  tacticalChallenge?: TacticalChallengeState;
  sharedOpponent?: SharedOpponent;
  lang: Language;
}) {
  const easterEggs = getEasterEggs(picks, lang);
  const chronicle = getChronicle(picks, result, lang);
  const runIdentity = getRunIdentity(picks, result, formation, mode, lang);
  const seasonMoments = getSeasonMoments(picks, result, formation, mode, lang);
  const t = translations[lang] as any;
  const comparison = sharedOpponent ? compareSharedRuns(result, sharedOpponent.result, lang) : undefined;

  // Dynamic trophy icon based on performance
  const renderTrophy = () => {
    if (result.wins === 38) {
      return <Trophy size={64} className="trophy-icon gold-trophy" />;
    } else if (result.losses === 0) {
      return <Trophy size={64} className="trophy-icon silver-trophy" />;
    } else if (result.points >= 90) {
      return <Trophy size={64} className="trophy-icon scudetto-trophy" />;
    }
    return <Trophy size={64} className="trophy-icon bronze-trophy" />;
  };

  return (
    <div className="result-card">
      <div className="result-header">
        <div className="result-trophy-wrapper">
          {renderTrophy()}
        </div>
        <h3>{result.title}</h3>
        <p className="result-subtitle-desc">{result.detail}</p>
      </div>

      <div className="record" aria-label={t.recordSimulato}>
        <span>{result.wins}-{result.draws}-{result.losses}</span>
      </div>
      <p className="points-detail">{result.points} {translations[lang].punti}</p>

      <section className="result-identity" aria-label={translations[lang].runIdentityTitle}>
        <div className="identity-badge">
          <span>{translations[lang].runIdentityTitle}</span>
          <strong>{runIdentity.title}</strong>
          <p>{runIdentity.detail}</p>
        </div>
        <div className="season-moments">
          <span>{translations[lang].seasonMomentsTitle}</span>
          <ul>
            {seasonMoments.map((moment) => (
              <li key={moment}>{moment}</li>
            ))}
          </ul>
        </div>
      </section>

      {sharedOpponent && comparison && (
        <section className="shared-comparison" aria-label={t.sharedComparisonTitle}>
          <div className="shared-comparison-heading">
            <span>{t.sharedComparisonTitle}</span>
            <strong>{comparison.title}</strong>
          </div>
          <div className="shared-comparison-scoreboard">
            <div>
              <span>{t.yourRunLabel}</span>
              <strong>{result.wins}-{result.draws}-{result.losses}</strong>
              <small>{result.points} {translations[lang].punti} · {translations[lang].valutazione} {result.rating}</small>
            </div>
            <div>
              <span>{t.sharedRunLabel}</span>
              <strong>{sharedOpponent.result.wins}-{sharedOpponent.result.draws}-{sharedOpponent.result.losses}</strong>
              <small>{sharedOpponent.result.points} {translations[lang].punti} · {translations[lang].valutazione} {sharedOpponent.result.rating}</small>
            </div>
          </div>
          <p>{comparison.detail}</p>
        </section>
      )}

      <dl className="result-metrics" aria-label={t.dettaglioSimulazione}>
        <div className="metric-box">
          <dt>{translations[lang].valutazione}</dt>
          <dd>{result.rating}</dd>
        </div>
        <div className="metric-box">
          <dt>{translations[lang].intesa}</dt>
          <dd>+{result.chemistry}</dd>
        </div>
        <div className="metric-box">
          <dt>{translations[lang].attacco}</dt>
          <dd>+{result.attackBonus}</dd>
        </div>
        <div className="metric-box">
          <dt>{translations[lang].mvp}</dt>
          <dd>{result.topPlayer}</dd>
        </div>
        {tacticalChallenge && (
          <div className={tacticalChallenge.complete ? "metric-box metric-success" : "metric-box metric-warning"}>
            <dt>{translations[lang].tacticalResultLabel}</dt>
            <dd>{tacticalChallenge.complete ? translations[lang].tacticalResultComplete : tacticalChallenge.progress}</dd>
          </div>
        )}
      </dl>

      {easterEggs.length > 0 && (
        <div className="eggs" aria-label={translations[lang].easterEggNostalgici}>
          {easterEggs.map((egg) => (
            <span className="egg-pill" key={egg}>{egg}</span>
          ))}
        </div>
      )}

      <div className="chronicle" aria-label={translations[lang].cronacaStagione}>
        <strong>{translations[lang].cronacaStagione}</strong>
        <ul>
          {chronicle.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>

      <div className="result-actions-strip">
        <button className="recap-button reset-primary" type="button" onClick={onReset} aria-label={translations[lang].ricominciaDraft}>
          <RotateCcw size={17} />
          <span>{translations[lang].ricominciaDraft}</span>
        </button>

        <button className="recap-button primary challenge-primary" type="button" onClick={onCopyChallenge} aria-label={translations[lang].copyChallengeLink}>
          <Share2 size={17} />
          <span>{challengeCopied ? translations[lang].challengeLinkCopied : translations[lang].copyChallengeLink}</span>
        </button>

        <button className="recap-button tertiary" type="button" onClick={onFeedback} aria-label={translations[lang].feedbackOpen}>
          <MessageSquare size={17} />
          <span>{translations[lang].feedbackOpen}</span>
        </button>
      </div>
    </div>
  );
}

function SharedChallengeBanner({
  lang,
  opponent,
  pickedCount,
  progress,
  totalSlots,
}: {
  lang: Language;
  opponent: SharedOpponent;
  pickedCount: number;
  progress: number;
  totalSlots: number;
}) {
  const t = translations[lang] as any;
  const targetRecord = `${opponent.result.wins}-${opponent.result.draws}-${opponent.result.losses}`;

  return (
    <section className="shared-active-banner" aria-label={t.sharedActiveTitle}>
      <div className="shared-active-copy">
        <span>{t.sharedActiveTitle}</span>
        <strong>{t.sharedActiveTargetTitle}</strong>
      </div>
      <div className="shared-active-target" aria-label={t.sharedActiveTargetTitle}>
        <strong>{targetRecord}</strong>
        <span>{t.sharedActiveTargetSummary(opponent.result.points, opponent.result.rating)}</span>
      </div>
      <div className="shared-active-progress">
        <span>{t.sharedActiveProgress(pickedCount, totalSlots, progress)}</span>
        <div className="shared-active-meter" aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </div>
      </div>
    </section>
  );
}

function compareSharedRuns(result: SeasonResult, opponent: SharedOpponentResult, lang: Language) {
  const t = translations[lang] as any;
  const resultScore = result.points * 1000 + result.rating * 10 + result.chemistry;
  const opponentScore = opponent.points * 1000 + opponent.rating * 10 + opponent.chemistry;

  if (resultScore > opponentScore) {
    return {
      title: t.sharedComparisonWon,
      detail: t.sharedComparisonDetail(result.points - opponent.points, result.rating - opponent.rating),
    };
  }

  if (resultScore < opponentScore) {
    return {
      title: t.sharedComparisonLost,
      detail: t.sharedComparisonDetail(result.points - opponent.points, result.rating - opponent.rating),
    };
  }

  return {
    title: t.sharedComparisonDraw,
    detail: t.sharedComparisonTieDetail,
  };
}

function FeedbackDialog({
  category,
  email,
  lang,
  message,
  onCategoryChange,
  onClose,
  onEmailChange,
  onMessageChange,
  onSubmit,
  status,
}: {
  category: string;
  email: string;
  lang: Language;
  message: string;
  onCategoryChange: (category: string) => void;
  onClose: () => void;
  onEmailChange: (email: string) => void;
  onMessageChange: (message: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  status: FeedbackStatus;
}) {
  const t = translations[lang] as any;
  const categories = [
    { value: "gameplay", label: t.feedbackCategoryGameplay },
    { value: "data", label: t.feedbackCategoryData },
    { value: "bug", label: t.feedbackCategoryBug },
    { value: "other", label: t.feedbackCategoryOther },
  ];

  return (
    <div className="feedback-overlay" role="presentation" onMouseDown={onClose}>
      <section
        aria-labelledby="feedback-title"
        aria-modal="true"
        className="feedback-dialog"
        role="dialog"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="feedback-dialog-header">
          <div>
            <p className="eyebrow">{t.feedbackEyebrow}</p>
            <h2 id="feedback-title">{t.feedbackTitle}</h2>
          </div>
          <button className="feedback-close" type="button" onClick={onClose} aria-label={t.feedbackClose}>
            <X size={18} />
          </button>
        </div>

        <form className="feedback-form" name="site-feedback" method="POST" data-netlify="true" onSubmit={onSubmit}>
          <input type="hidden" name="form-name" value="site-feedback" />
          <label>
            <span>{t.feedbackCategoryLabel}</span>
            <select name="category" value={category} onChange={(event) => onCategoryChange(event.target.value)}>
              {categories.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>{t.feedbackMessageLabel}</span>
            <textarea
              name="message"
              required
              rows={5}
              value={message}
              onChange={(event) => onMessageChange(event.target.value)}
              placeholder={t.feedbackMessagePlaceholder}
            />
          </label>

          <label>
            <span>{t.feedbackEmailLabel}</span>
            <input
              name="email"
              type="email"
              value={email}
              onChange={(event) => onEmailChange(event.target.value)}
              placeholder={t.feedbackEmailPlaceholder}
            />
          </label>

          <div className="feedback-form-actions">
            <button className="feedback-cancel" type="button" onClick={onClose}>
              {t.feedbackCancel}
            </button>
            <button className="feedback-submit" type="submit" disabled={status === "submitting"}>
              {status === "submitting" ? <Sparkles size={16} className="animate-spin" /> : <Send size={16} />}
              <span>{status === "submitting" ? t.feedbackSending : t.feedbackSubmit}</span>
            </button>
          </div>

          {status === "sent" && <p className="feedback-status success">{t.feedbackSuccess}</p>}
          {status === "error" && <p className="feedback-status error">{t.feedbackError}</p>}
        </form>
      </section>
    </div>
  );
}

function getFeedbackContext({
  mode,
  formation,
  favoriteClub,
  picks,
  result,
  lang,
  source,
  challengeId,
}: {
  mode: GameMode;
  formation: Formation;
  favoriteClub?: Club;
  picks: DraftPick[];
  result: SeasonResult | null;
  lang: Language;
  source: FeedbackSource;
  challengeId: DraftChallengeId;
}) {
  const pickedPlayers = picks.map((pick) => `${pick.slot.id}:${pick.player.name} (${pick.player.club} ${pick.player.season})`);
  return JSON.stringify({
    lang,
    source,
    challengeId,
    mode: mode.id,
    modeName: mode.name,
    favoriteClub,
    formation: formation.id,
    picks: picks.length,
    pickedPlayers,
    result: result ? `${result.wins}-${result.draws}-${result.losses}, ${result.points} pts, rating ${result.rating}` : null,
  });
}

const ICON_RARITY_PEAK_THRESHOLD = 99;
const CHAMPION_RARITY_PEAK_THRESHOLD = 96;
const RARE_RARITY_PEAK_THRESHOLD = 92;

function getRarityClass(player: PlayerSeason): DraftRarity {
  const profile = getPlayerProfile(player);
  const peak = Math.max(player.score, profile.legacy, profile.finishing, profile.creativity, profile.defending);
  if (peak >= ICON_RARITY_PEAK_THRESHOLD) return "icona";
  if (peak >= CHAMPION_RARITY_PEAK_THRESHOLD) return "campione";
  if (peak >= RARE_RARITY_PEAK_THRESHOLD || (player.source && player.score >= 88) || (profile.dataLevel === "curated-high" && peak >= 89)) return "rara";
  return "comune";
}

function getTacticalSortedOffers(offers: DraftOffer[], formation: Formation, picks: DraftPick[]) {
  return [...offers].sort((left, right) => {
    const leftFit = getTacticalFitSlots(left, formation, picks).length;
    const rightFit = getTacticalFitSlots(right, formation, picks).length;
    if (leftFit !== rightFit) {
      return rightFit - leftFit;
    }
    return 0;
  });
}

function getTacticalFitSlots(offer: DraftOffer, formation: Formation, picks: DraftPick[]) {
  const filledSlotIds = new Set(picks.map((pick) => pick.slot.id));
  return getSlotOptions(offer).filter((slot) =>
    !filledSlotIds.has(slot.id) &&
    isTacticalFitPick({ slot, player: offer.player }, formation),
  );
}

type LocalizedText = Record<Language, string>;
type LocalizedTitleBody = Record<Language, { title: string; body: string }>;

const tacticalChallengeCopy: Record<string, LocalizedTitleBody> = {
  "352": {
    it: { title: "Quinti in pressione", body: "Completa entrambi i quinti con work-rate alto." },
    en: { title: "Pressing wing-backs", body: "Fill both wing-back slots with high-work-rate players." },
    es: { title: "Carrileros en presión", body: "Completa ambos carrileros con jugadores de alto trabajo." },
    "pt-BR": { title: "Alas em pressão", body: "Complete os dois alas com jogadores de alto ritmo de trabalho." },
    fr: { title: "Pistons sous pression", body: "Complète les deux pistons avec des joueurs à fort volume de travail." },
  },
  "4321": {
    it: { title: "Due mezze punte creative", body: "Le due mezze punte devono creare gioco dietro la punta." },
    en: { title: "Two creative half-forwards", body: "Both half-forward slots need real creators behind the striker." },
    es: { title: "Dos medias puntas creativas", body: "Las dos medias puntas deben crear juego detrás del delantero." },
    "pt-BR": { title: "Dois meias-atacantes criativos", body: "Os dois meias-atacantes precisam criar jogo atrás do centroavante." },
    fr: { title: "Deux soutiens créatifs", body: "Les deux soutiens doivent créer derrière l'attaquant." },
  },
  "4231": {
    it: { title: "Doppio mediano vero", body: "I due mediani devono proteggere e far uscire palla." },
    en: { title: "True double pivot", body: "Both pivots must protect the line and move the ball out." },
    es: { title: "Doble pivote real", body: "Los dos mediocentros deben proteger y sacar la pelota." },
    "pt-BR": { title: "Duplo volante de verdade", body: "Os dois volantes precisam proteger a linha e sair jogando." },
    fr: { title: "Vrai double pivot", body: "Les deux milieux doivent protéger et ressortir le ballon." },
  },
  "442": {
    it: { title: "Catena operaia", body: "Almeno due centrocampisti o esterni devono fare lavoro sporco." },
    en: { title: "Hard-working chains", body: "At least two midfielders or wide mids must bring dirty work." },
    es: { title: "Bandas obreras", body: "Al menos dos centrocampistas o bandas deben hacer el trabajo sucio." },
    "pt-BR": { title: "Corredores operários", body: "Ao menos dois meias ou jogadores de lado precisam fazer o trabalho sujo." },
    fr: { title: "Couloirs travailleurs", body: "Au moins deux milieux ou excentrés doivent faire le travail de l'ombre." },
  },
  "433": {
    it: { title: "Ampiezza e regia", body: "Serve un regista vero e almeno un esterno offensivo pericoloso." },
    en: { title: "Width and regista", body: "You need a real regista and at least one dangerous wide forward." },
    es: { title: "Amplitud y regista", body: "Hace falta un regista real y al menos un extremo peligroso." },
    "pt-BR": { title: "Amplitude e regista", body: "É preciso um regista real e ao menos um ponta perigoso." },
    fr: { title: "Largeur et regista", body: "Il faut un vrai regista et au moins un ailier dangereux." },
  },
  "4312": {
    it: { title: "Trequarti e regia", body: "Il trequartista deve creare, il regista deve governare." },
    en: { title: "Playmaker and regista", body: "The playmaker must create, and the regista must control tempo." },
    es: { title: "Mediapunta y regia", body: "El mediapunta debe crear y el regista debe gobernar el ritmo." },
    "pt-BR": { title: "Meia central e regência", body: "O meia central precisa criar, e o regista precisa comandar o ritmo." },
    fr: { title: "Meneur et regista", body: "Le meneur doit créer et le regista doit contrôler le tempo." },
  },
};

function getLocalizedTitleBody(copy: Record<string, LocalizedTitleBody>, key: string, lang: Language) {
  const fallback = copy[key] ?? Object.values(copy)[0];
  return fallback ? fallback[lang] ?? fallback.en : { title: "", body: "" };
}

function getTacticalChallenge(formation: Formation, picks: DraftPick[], lang: Language): TacticalChallengeState {
  const count = (slotIds: string[], predicate: (pick: DraftPick) => boolean) =>
    slotIds.filter((slotId) => {
      const pick = picks.find((item) => item.slot.id === slotId);
      return pick ? predicate(pick) : false;
    }).length;
  const has = (slotId: string, predicate: (pick: DraftPick) => boolean) => {
    const pick = picks.find((item) => item.slot.id === slotId);
    return Boolean(pick && predicate(pick));
  };
  const wideAttack = ["lw", "rw"].some((slotId) => has(slotId, isWideAttackerPick));

  let current: number;
  let target = 2;
  let copyKey = "4312";

  switch (formation.id) {
    case "352":
      copyKey = "352";
      current = count(["lwb", "rwb"], isWideRunnerPick);
      break;
    case "4321":
      copyKey = "4321";
      current = count(["am1", "am2"], isCreatorPick);
      break;
    case "4231":
      copyKey = "4231";
      current = count(["dm1", "dm2"], isShieldPick);
      break;
    case "442":
      copyKey = "442";
      current = Math.min(2, count(["lm", "rm", "cm1", "cm2"], isGrinderPick));
      break;
    case "433":
      copyKey = "433";
      current = Number(has("regista", isRegistaPick)) + Number(wideAttack);
      break;
    default:
      current = Number(has("trequartista", isCreatorPick)) + Number(has("regista", isRegistaPick));
      break;
  }
  const copy = getLocalizedTitleBody(tacticalChallengeCopy, copyKey, lang);

  return {
    id: formation.id,
    title: copy.title,
    body: copy.body,
    current,
    target,
    complete: current >= target,
    progress: `${Math.min(current, target)}/${target}`,
  };
}

function isTacticalFitPick(pick: DraftPick, formation: Formation) {
  switch (formation.id) {
    case "352":
      return ["lwb", "rwb"].includes(pick.slot.id) && isWideRunnerPick(pick);
    case "4321":
      return ["am1", "am2"].includes(pick.slot.id) && isCreatorPick(pick);
    case "4231":
      return ["dm1", "dm2"].includes(pick.slot.id) && isShieldPick(pick);
    case "442":
      return ["lm", "rm", "cm1", "cm2"].includes(pick.slot.id) && isGrinderPick(pick);
    case "433":
      return (pick.slot.id === "regista" && isRegistaPick(pick)) || (["lw", "rw"].includes(pick.slot.id) && isWideAttackerPick(pick));
    default:
      return (pick.slot.id === "trequartista" && isCreatorPick(pick)) || (pick.slot.id === "regista" && isRegistaPick(pick));
  }
}

function normalizeName(name: string) {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function getOfferKey(offer: DraftOffer) {
  return getOfferKeyForSlot(offer, offer.slot);
}

function getOfferKeyForSlot(offer: DraftOffer, slot: LineupSlot) {
  return `${slot.id}:${offer.player.id}`;
}

function getSlotOptions(offer: DraftOffer) {
  return uniqueBy([offer.slot, ...(offer.slotOptions ?? [])], (slot) => slot.id);
}

function getRoleMoveTargets(pick: DraftPick, formationSlots: LineupSlot[], picks: DraftPick[]) {
  const filledSlotIds = new Set(picks.map((item) => item.slot.id));
  return formationSlots.filter((slot) =>
    slot.id !== pick.slot.id &&
    !filledSlotIds.has(slot.id) &&
    isSlotCompatible(pick.player, slot),
  );
}

function resolveOfferKey(offers: DraftOffer[], offerKey: string | null): DraftOffer | undefined {
  if (!offerKey) {
    return undefined;
  }

  const [slotId, playerId] = offerKey.split(":");
  const offer = offers.find((item) => item.player.id === playerId && getSlotOptions(item).some((slot) => slot.id === slotId));
  const slot = offer ? getSlotOptions(offer).find((option) => option.id === slotId) : undefined;

  return offer && slot ? { ...offer, slot } : undefined;
}

function tSlotLabel(slot: LineupSlot, lang: Language) {
  return (translations[lang] as any)[slot.id] || slot.label;
}

function uniqueBy<T>(items: T[], getKey: (item: T) => string) {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = getKey(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function makeDraftSeed() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function createSharedRunUrl({
  mode,
  formation,
  challengeId,
  favoriteClub,
  draftSeed,
  opponent,
}: {
  mode: GameMode;
  formation: Formation;
  challengeId: DraftChallengeId;
  favoriteClub?: Club;
  draftSeed: string;
  opponent?: SharedOpponent;
}) {
  const payload: SharedRunPayload = {
    v: 1,
    seed: draftSeed,
    modeId: mode.id,
    formationId: formation.id,
    challengeId,
    favoriteClub,
    opponent,
  };
  const encoded = encodeBase64Url(JSON.stringify(payload));

  if (typeof window === "undefined") {
    return `${siteUrl}?run=${encoded}`;
  }

  const url = new URL(window.location.href);
  url.searchParams.set("run", encoded);
  url.searchParams.delete("lang");
  return url.toString();
}

function parseSharedRun(): ParsedSession | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const rawRun = new URLSearchParams(window.location.search).get("run");
  if (!rawRun) {
    return undefined;
  }

  try {
    const payload = JSON.parse(decodeBase64Url(rawRun)) as Partial<SharedRunPayload>;
    if (
      payload.v !== 1 ||
      !payload.seed ||
      !payload.modeId ||
      !payload.formationId ||
      !modes.some((mode) => mode.id === payload.modeId) ||
      !formations.some((formation) => formation.id === payload.formationId)
    ) {
      return undefined;
    }

    return {
      modeId: payload.modeId,
      formationId: payload.formationId,
      challengeId: payload.challengeId === "tactical" ? "tactical" : "standard",
      favoriteClub: payload.favoriteClub,
      draftSeed: payload.seed,
      pickedPlayerIds: [],
      pickedSlotIds: [],
      sharedOpponent: parseSharedOpponent(payload.opponent),
    };
  } catch {
    return undefined;
  }
}

function createSharedOpponent(picks: DraftPick[], result: SeasonResult): SharedOpponent {
  return {
    picks: picks.map((pick) => ({ playerId: pick.player.id, slotId: pick.slot.id })),
    result: {
      wins: result.wins,
      draws: result.draws,
      losses: result.losses,
      points: result.points,
      rating: result.rating,
      chemistry: result.chemistry,
      attackBonus: result.attackBonus,
      topPlayer: result.topPlayer,
    },
  };
}

function parseSharedOpponent(opponent: unknown): SharedOpponent | undefined {
  if (!opponent || typeof opponent !== "object") {
    return undefined;
  }

  const candidate = opponent as Partial<SharedOpponent>;
  const result = candidate.result as Partial<SharedOpponentResult> | undefined;
  if (!Array.isArray(candidate.picks) || !result) {
    return undefined;
  }

  const parsedPicks = candidate.picks
    .map((pick) => {
      if (!pick || typeof pick !== "object") {
        return undefined;
      }
      const rawPick = pick as Partial<{ playerId: string; slotId: string }>;
      return typeof rawPick.playerId === "string" && typeof rawPick.slotId === "string"
        ? { playerId: rawPick.playerId, slotId: rawPick.slotId }
        : undefined;
    })
    .filter((pick) => pick !== undefined);

  if (parsedPicks.length === 0) {
    return undefined;
  }

  const numericFields: Array<keyof Omit<SharedOpponentResult, "topPlayer">> = [
    "wins",
    "draws",
    "losses",
    "points",
    "rating",
    "chemistry",
    "attackBonus",
  ];
  if (!numericFields.every((key) => typeof result[key] === "number") || typeof result.topPlayer !== "string") {
    return undefined;
  }

  const wins = result.wins;
  const draws = result.draws;
  const losses = result.losses;
  const points = result.points;
  const rating = result.rating;
  const chemistry = result.chemistry;
  const attackBonus = result.attackBonus;
  const topPlayer = result.topPlayer;
  if (
    typeof wins !== "number" ||
    typeof draws !== "number" ||
    typeof losses !== "number" ||
    typeof points !== "number" ||
    typeof rating !== "number" ||
    typeof chemistry !== "number" ||
    typeof attackBonus !== "number" ||
    typeof topPlayer !== "string"
  ) {
    return undefined;
  }

  return {
    picks: parsedPicks,
    result: {
      wins,
      draws,
      losses,
      points,
      rating,
      chemistry,
      attackBonus,
      topPlayer,
    },
  };
}

function encodeBase64Url(value: string) {
  return btoa(value)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function decodeBase64Url(value: string) {
  const padded = `${value.replace(/-/g, "+").replace(/_/g, "/")}${"=".repeat((4 - (value.length % 4)) % 4)}`;
  return atob(padded);
}

function normalizePlayerName(name: string) {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function calculateCurrentChemistry(picks: DraftPick[]): number {
  if (picks.length === 0) return 0;
  const clubCounts = picks.reduce<Record<string, number>>((acc, pick) => {
    acc[pick.player.club] = (acc[pick.player.club] ?? 0) + 1;
    return acc;
  }, {});
  const eraCounts = picks.reduce<Record<string, number>>((acc, pick) => {
    acc[pick.player.era] = (acc[pick.player.era] ?? 0) + 1;
    return acc;
  }, {});

  const chem =
    Object.values(clubCounts).filter((count) => count >= 2).length * 1.4 +
    Object.values(eraCounts).filter((count) => count >= 3).length * 1.1;
  return Math.round(chem * 10) / 10;
}

const runIdentityCopy: Record<string, LocalizedTitleBody> = {
  historicSpine: {
    it: { title: "Ossatura Storica", body: "La run ha trovato un blocco club-stagione riconoscibile." },
    en: { title: "Historic Spine", body: "The run found a clear club-season spine." },
    es: { title: "Columna histórica", body: "La run encontró un bloque club-temporada reconocible." },
    "pt-BR": { title: "Espinha histórica", body: "A run encontrou um bloco clube-temporada reconhecível." },
    fr: { title: "Ossature historique", body: "La run a trouvé un bloc club-saison clairement identifiable." },
  },
  christmasTree: {
    it: { title: "Albero Di Natale", body: "Due rifinitori, una punta e tanta pazienza da calcio italiano." },
    en: { title: "Christmas Tree", body: "Two creators, one striker, and a patient Italian-football shape." },
    es: { title: "Árbol de Navidad", body: "Dos creadores, un delantero y mucha paciencia de calcio italiano." },
    "pt-BR": { title: "Árvore de Natal", body: "Dois criadores, um centroavante e muita paciência de futebol italiano." },
    fr: { title: "Sapin de Noël", body: "Deux créateurs, un attaquant et beaucoup de patience à l'italienne." },
  },
  wingbacksFire: {
    it: { title: "Quinti In Fiamme", body: "Tre centrali e corsie lunghe: la partita vive sulle fasce." },
    en: { title: "Wing-backs on Fire", body: "Three center-backs and long wide lanes: the match lives on the flanks." },
    es: { title: "Carrileros encendidos", body: "Tres centrales y carriles largos: el partido vive por las bandas." },
    "pt-BR": { title: "Alas em chamas", body: "Três zagueiros e corredores longos: o jogo vive pelas laterais." },
    fr: { title: "Pistons en feu", body: "Trois centraux et de longs couloirs : le match vit sur les ailes." },
  },
  purePlaymaking: {
    it: { title: "Trequartismo Puro", body: "La squadra si accende tra passaggi, assist e invenzioni." },
    en: { title: "Pure Playmaking", body: "The squad runs on passing, assists, and invention." },
    es: { title: "Trequartismo puro", body: "El equipo se enciende con pases, asistencias e invenciones." },
    "pt-BR": { title: "Trequartismo puro", body: "O time ganha vida com passes, assistências e invenção." },
    fr: { title: "Trequartismo pur", body: "L'équipe s'allume par les passes, les offrandes et l'invention." },
  },
  workerBand: {
    it: { title: "Banda Operaia", body: "Meno copertina, piu seconde palle e lavoro senza riflettori." },
    en: { title: "Worker Band", body: "Less cover-star glamour, more second balls and hidden work." },
    es: { title: "Banda obrera", body: "Menos portada, más segundas jugadas y trabajo sin focos." },
    "pt-BR": { title: "Banda operária", body: "Menos capa de revista, mais segunda bola e trabalho longe dos holofotes." },
    fr: { title: "Bande ouvrière", body: "Moins de glamour, plus de deuxièmes ballons et de travail invisible." },
  },
  scudettoArmy: {
    it: { title: "Armata Scudetto", body: "Una XI da discutere, difendere e rilanciare dopo ogni simulazione." },
    en: { title: "Scudetto Army", body: "An XI worth debating, defending, and replaying after every simulation." },
    es: { title: "Armada Scudetto", body: "Un XI para debatir, defender y volver a lanzar tras cada simulación." },
    "pt-BR": { title: "Armada Scudetto", body: "Um XI para discutir, defender e rodar de novo após cada simulação." },
    fr: { title: "Armada Scudetto", body: "Un XI à débattre, défendre et relancer après chaque simulation." },
  },
  barSportTeam: {
    it: { title: "Squadra Da Bar Sport", body: "Una XI da discutere, difendere e rilanciare dopo ogni simulazione." },
    en: { title: "Bar Sport XI", body: "An XI worth debating, defending, and replaying after every simulation." },
    es: { title: "Equipo de bar deportivo", body: "Un XI para debatir, defender y volver a lanzar tras cada simulación." },
    "pt-BR": { title: "Time de mesa de bar", body: "Um XI para discutir, defender e rodar de novo após cada simulação." },
    fr: { title: "Équipe de comptoir", body: "Un XI à débattre, défendre et relancer après chaque simulation." },
  },
};

const seasonMomentCopy: Record<string, LocalizedText> = {
  milanDerby: {
    it: "Giornata 7: derby di Milano con rumore da partita sporca.",
    en: "Matchday 7: Milan derby noise, tense and messy.",
    es: "Jornada 7: derbi de Milán con ruido de partido sucio.",
    "pt-BR": "Rodada 7: dérbi de Milão com clima de jogo travado.",
    fr: "Journée 7 : derby de Milan, bruyant et accroché.",
  },
  romanDerby: {
    it: "Giornata 7: derby romano, nervi e talento nello stesso minuto.",
    en: "Matchday 7: Roman derby, nerves and talent in the same minute.",
    es: "Jornada 7: derbi romano, nervios y talento en el mismo minuto.",
    "pt-BR": "Rodada 7: dérbi romano, nervos e talento no mesmo minuto.",
    fr: "Journée 7 : derby romain, nerfs et talent dans la même minute.",
  },
  awayTest: {
    it: "Giornata 7: prima trasferta vera, la curva misura il carattere.",
    en: "Matchday 7: first serious away test, the fans measure the squad's character.",
    es: "Jornada 7: primera salida seria, la grada mide el carácter.",
    "pt-BR": "Rodada 7: primeira visita séria, a torcida mede o caráter.",
    fr: "Journée 7 : premier vrai déplacement, la tribune juge le caractère.",
  },
  creators: {
    it: "Giornata 18: i creatori trovano spazio tra le linee.",
    en: "Matchday 18: the creators find space between the lines.",
    es: "Jornada 18: los creadores encuentran espacio entre líneas.",
    "pt-BR": "Rodada 18: os criadores encontram espaço entre as linhas.",
    fr: "Journée 18 : les créateurs trouvent l'espace entre les lignes.",
  },
  wingbacks: {
    it: "Giornata 18: i quinti ribaltano il campo e cambiano ritmo.",
    en: "Matchday 18: the wing-backs flip the field and change the rhythm.",
    es: "Jornada 18: los carrileros cambian el campo y el ritmo.",
    "pt-BR": "Rodada 18: os alas viram o campo e mudam o ritmo.",
    fr: "Journée 18 : les pistons renversent le terrain et changent le rythme.",
  },
  spine: {
    it: "Giornata 18: partita bloccata, decide l'intesa della spina dorsale.",
    en: "Matchday 18: a locked match is decided by the squad spine.",
    es: "Jornada 18: partido cerrado, decide la química de la columna vertebral.",
    "pt-BR": "Rodada 18: jogo bloqueado, decide o entrosamento da espinha dorsal.",
    fr: "Journée 18 : match verrouillé, l'ossature fait la différence.",
  },
  provincia: {
    it: "Giornata 31: la provincia sente odore di impresa.",
    en: "Matchday 31: the provincial dream starts to feel real.",
    es: "Jornada 31: la provincia empieza a oler la hazaña.",
    "pt-BR": "Rodada 31: a província começa a sentir cheiro de façanha.",
    fr: "Journée 31 : la province commence à sentir l'exploit.",
  },
  nostalgia: {
    it: "Giornata 31: notte da VHS, numero dieci e difesa bassa.",
    en: "Matchday 31: VHS-night football, number tens and a low block.",
    es: "Jornada 31: noche de VHS, número diez y bloque bajo.",
    "pt-BR": "Rodada 31: noite de VHS, camisa 10 e bloco baixo.",
    fr: "Journée 31 : soirée VHS, numéro dix et bloc bas.",
  },
  modern: {
    it: "Giornata 31: il draft moderno entra nella corsa scudetto.",
    en: "Matchday 31: the modern draft enters the title race.",
    es: "Jornada 31: el draft moderno entra en la carrera por el scudetto.",
    "pt-BR": "Rodada 31: o draft moderno entra na corrida pelo scudetto.",
    fr: "Journée 31 : le draft moderne entre dans la course au scudetto.",
  },
  perfect: {
    it: "Giornata 38: la curva invade la storia del 38-0.",
    en: "Matchday 38: the fans rush into 38-0 history.",
    es: "Jornada 38: la grada invade la historia del 38-0.",
    "pt-BR": "Rodada 38: a torcida invade a história do 38-0.",
    fr: "Journée 38 : la tribune entre dans l'histoire du 38-0.",
  },
  unbeaten: {
    it: "Giornata 38: niente sconfitte, sciarpe alte fino al fischio finale.",
    en: "Matchday 38: unbeaten, scarves raised until the final whistle.",
    es: "Jornada 38: invictos, bufandas arriba hasta el pitido final.",
    "pt-BR": "Rodada 38: invicto, cachecóis erguidos até o apito final.",
    fr: "Journée 38 : invaincu, écharpes levées jusqu'au coup de sifflet final.",
  },
  improve: {
    it: "Giornata 38: resta una run da migliorare, ma il bar ne discute gia.",
    en: "Matchday 38: still a run to improve, but the bar is already debating it.",
    es: "Jornada 38: queda una run por mejorar, pero el bar ya la discute.",
    "pt-BR": "Rodada 38: ainda dá para melhorar a run, mas o bar já está debatendo.",
    fr: "Journée 38 : une run à améliorer, mais le comptoir en parle déjà.",
  },
};

function getLocalizedText(copy: Record<string, LocalizedText>, key: string, lang: Language) {
  return copy[key]?.[lang] ?? copy[key]?.en ?? "";
}

function getRunIdentity(picks: DraftPick[], result: SeasonResult, formation: Formation, mode: GameMode, lang: Language) {
  const sameSeasonMax = maxCount(picks.map((pick) => `${pick.player.club}-${pick.player.season}`));
  const creatorCount = picks.filter(isCreatorPick).length;
  const grinderCount = picks.filter(isGrinderPick).length;
  const iconCount = picks.filter((pick) => isIconPick(pick.player)).length;

  if (sameSeasonMax >= 3) {
    return getRunIdentityCopy("historicSpine", lang);
  }

  if (formation.id === "4321") {
    return getRunIdentityCopy("christmasTree", lang);
  }

  if (formation.id === "352") {
    return getRunIdentityCopy("wingbacksFire", lang);
  }

  if (creatorCount >= 3) {
    return getRunIdentityCopy("purePlaymaking", lang);
  }

  if (grinderCount >= 3 && iconCount <= 1) {
    return getRunIdentityCopy("workerBand", lang);
  }

  return getRunIdentityCopy(result.losses === 0 ? "scudettoArmy" : "barSportTeam", lang);
}

function getRunIdentityCopy(key: string, lang: Language) {
  const copy = getLocalizedTitleBody(runIdentityCopy, key, lang);
  return {
    title: copy.title,
    detail: copy.body,
  };
}

function getSeasonMoments(picks: DraftPick[], result: SeasonResult, formation: Formation, mode: GameMode, lang: Language) {
  const clubs = new Set(picks.map((pick) => pick.player.club));
  const moments: string[] = [];

  if (clubs.has("Milan") && clubs.has("Inter")) {
    moments.push(getLocalizedText(seasonMomentCopy, "milanDerby", lang));
  } else if (clubs.has("Roma") && clubs.has("Lazio")) {
    moments.push(getLocalizedText(seasonMomentCopy, "romanDerby", lang));
  } else {
    moments.push(getLocalizedText(seasonMomentCopy, "awayTest", lang));
  }

  if (formation.id === "4321" || formation.id === "4231") {
    moments.push(getLocalizedText(seasonMomentCopy, "creators", lang));
  } else if (formation.id === "352") {
    moments.push(getLocalizedText(seasonMomentCopy, "wingbacks", lang));
  } else {
    moments.push(getLocalizedText(seasonMomentCopy, "spine", lang));
  }

  if (mode.id.includes("nostalgia")) {
    moments.push(getLocalizedText(seasonMomentCopy, "nostalgia", lang));
  } else {
    moments.push(getLocalizedText(seasonMomentCopy, "modern", lang));
  }

  moments.push(
    result.wins === 38
      ? getLocalizedText(seasonMomentCopy, "perfect", lang)
      : result.losses === 0
        ? getLocalizedText(seasonMomentCopy, "unbeaten", lang)
        : getLocalizedText(seasonMomentCopy, "improve", lang),
  );

  return moments;
}

function isCreatorPick(pick: DraftPick) {
  const profile = getPlayerProfile(pick.player);
  return (pick.player.assists ?? 0) >= 5 || profile.creativity >= 90 || profile.passing >= 90 || pick.slot.id.includes("am") || pick.slot.id === "trequartista";
}

function isGrinderPick(pick: DraftPick) {
  return pick.player.score <= 86 && getPlayerProfile(pick.player).workRate >= 88;
}

function isWideRunnerPick(pick: DraftPick) {
  const profile = getPlayerProfile(pick.player);
  return profile.workRate >= 84 && (profile.pace >= 74 || profile.physical >= 78);
}

function isShieldPick(pick: DraftPick) {
  const profile = getPlayerProfile(pick.player);
  return profile.workRate >= 84 && (profile.defending >= 82 || profile.passing >= 84);
}

function isRegistaPick(pick: DraftPick) {
  const profile = getPlayerProfile(pick.player);
  return profile.passing >= 85 && (profile.creativity >= 84 || profile.workRate >= 82);
}

function isWideAttackerPick(pick: DraftPick) {
  const profile = getPlayerProfile(pick.player);
  return profile.pace >= 84 || profile.creativity >= 86 || profile.finishing >= 86;
}

function isIconPick(player: PlayerSeason) {
  return getRarityClass(player) === "icona" || player.score >= 93;
}

function maxCount(values: string[]) {
  if (values.length === 0) {
    return 0;
  }

  return Math.max(...Object.values(countValues(values)));
}

function stableNumber(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0) / 4294967295;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function getEasterEggs(picks: DraftPick[], lang: Language) {
  const ids = new Set(picks.map((pick) => pick.player.id));
  const eras = new Set(picks.map((pick) => eraLabels[pick.player.era]));
  const eggs: string[] = [];

  if (ids.has("vialli-sam-90") && ids.has("mancini-sam-90")) {
    eggs.push(translations[lang].eggviallimancini);
  }

  if (ids.has("kvara-nap-22") && ids.has("osimhen-nap-22")) {
    eggs.push(translations[lang].eggkvaraosimhen);
  }

  if (
    (ids.has("lookman-ata-23") || ids.has("src22-lookman-atalanta")) &&
    (ids.has("osimhen-nap-22") || ids.has("src22-osimhen-napoli"))
  ) {
    eggs.push(translations[lang].egglookmanosimhen);
  }

  if (
    (ids.has("leao-mil-21") || ids.has("src22-leao-milan")) &&
    (ids.has("kaka-mil-06") || ids.has("src22-kvara-napoli"))
  ) {
    eggs.push(translations[lang].eggleaokaka);
  }

  if (ids.has("buffon-parma-98") && ids.has("thuram-parma-98")) {
    eggs.push(translations[lang].eggbuffonthuram);
  }

  if (ids.has("adriano-inter-04") && ids.has("kaka-mil-06")) {
    eggs.push(translations[lang].eggadrianokaka);
  }

  if (ids.has("juliocesar-inter-09") && ids.has("maicon-inter-09")) {
    eggs.push(translations[lang].eggjuliomaicon);
  }

  if (ids.has("totti-roma-00") && ids.has("cafu-roma-00")) {
    eggs.push(translations[lang].eggtotticafu);
  }

  if (eras.size >= 4) {
    eggs.push(translations[lang].eggmuseo);
  }

  return eggs;
}

function getChronicle(picks: DraftPick[], result: SeasonResult, lang: Language) {
  const ids = new Set(picks.map((pick) => pick.player.id));
  const clubs = countValues(picks.map((pick) => pick.player.club));
  const eras = countValues(picks.map((pick) => eraLabels[pick.player.era]));
  const countries = countValues(picks.map((pick) => pick.player.country));
  const mostCommonClub = topEntry(clubs);
  const mostCommonEra = topEntry(eras);
  const mostCommonCountry = topEntry(countries);
  const t = translations[lang] as any;
  
  const eraKey = picks.find(p => p.player.era === mostCommonEra[0])?.player.era || mostCommonEra[0];
  const formattedEra = t[`formatted_${eraKey}`] || mostCommonEra[0];
  
  const notes = [
    t.chronicleTopPlayer(result.topPlayer, result.rating),
    t.chronicleClubBlock(mostCommonClub[0], mostCommonClub[1]),
    t.chroniclePhilosophy(formattedEra),
  ];

  if (mostCommonCountry[1] >= 3) {
    notes.push(t.chronicleCountryColony(mostCommonCountry[0], mostCommonCountry[1]));
  }

  if (ids.has("maradona-nap-86")) {
    notes.push(t.chronicleMaradona);
  }

  if (ids.has("ronaldo-inter-97")) {
    notes.push(t.chronicleRonaldo);
  }

  if (ids.has("adriano-inter-04")) {
    notes.push(t.chronicleAdriano);
  }

  return notes.slice(0, 5);
}

function countValues(values: string[]) {
  return values.reduce<Record<string, number>>((counts, value) => {
    counts[value] = (counts[value] ?? 0) + 1;
    return counts;
  }, {});
}

function topEntry(counts: Record<string, number>) {
  return Object.entries(counts).sort((left, right) => right[1] - left[1])[0];
}

function loadInitialSession(): InitialSession {
  const defaultMode = modes.find((item) => item.id === "draft") ?? modes[0];
  const defaultFormation = formations.find((item) => item.id === defaultFormationId) ?? formations[0];

  if (typeof window === "undefined") {
    return { mode: defaultMode, formation: defaultFormation, challengeId: "standard" as DraftChallengeId, favoriteClub: defaultMode.clubs[0], draftSeed: makeDraftSeed(), picks: [] };
  }

  try {
    const sharedSession = parseSharedRun();
    const rawSession = window.localStorage.getItem(storageKey);
    if (!sharedSession && !rawSession) {
      return { mode: defaultMode, formation: defaultFormation, challengeId: "standard" as DraftChallengeId, favoriteClub: defaultMode.clubs[0], draftSeed: makeDraftSeed(), picks: [] };
    }

    const session = sharedSession ?? JSON.parse(rawSession as string) as ParsedSession;
    const savedModeId =
      session.modeId === "cuore"
        ? "draft"
        : session.modeId === "provincia"
          ? "nostalgia-hard"
          : session.modeId === "provincia-medium"
            ? "nostalgia"
            : session.modeId;
    const mode = modes.find((item) => item.id === savedModeId) ?? defaultMode;
    const formation = formations.find((item) => item.id === session.formationId) ?? defaultFormation;
    const challengeId: DraftChallengeId = session.challengeId === "tactical" ? "tactical" : "standard";
    const favoriteClub = session.favoriteClub && mode.clubs.includes(session.favoriteClub) ? session.favoriteClub : mode.clubs[0];
    const draftSeed = session.draftSeed || makeDraftSeed();
    const pickedPlayers = session.pickedPlayerIds
      .map((id) => players.find((player) => player.id === id))
      .filter((player) => player !== undefined);
    const usedSlotIds = new Set<string>();
    const picks = pickedPlayers
      .slice(0, formation.slots.length)
      .map((player, index): DraftPick | undefined => {
        const savedSlot = session.pickedSlotIds?.[index]
          ? formation.slots.find((slot) => slot.id === session.pickedSlotIds?.[index] && isSlotCompatible(player, slot))
          : undefined;
        const fallbackSlot = formation.slots.find((slot) => !usedSlotIds.has(slot.id) && isSlotCompatible(player, slot)) ?? formation.slots[index];
        const slot = savedSlot && !usedSlotIds.has(savedSlot.id) ? savedSlot : fallbackSlot;

        if (!slot || usedSlotIds.has(slot.id)) {
          return undefined;
        }

        usedSlotIds.add(slot.id);
        return { slot, player };
      })
      .filter((pick) => pick !== undefined);

    return { mode, formation, challengeId, favoriteClub, draftSeed, picks, sharedOpponent: session.sharedOpponent };
  } catch {
    return { mode: defaultMode, formation: defaultFormation, challengeId: "standard" as DraftChallengeId, favoriteClub: defaultMode.clubs[0], draftSeed: makeDraftSeed(), picks: [] };
  }
}

function getModeStory(storyId: ModeStoryId) {
  return modeStories.find((story) => story.id === storyId) ?? modeStories[0];
}

function getModeVariant(modeId: string): { storyId: ModeStoryId; difficulty: ModeDifficultyId } {
  for (const story of modeStories) {
    for (const difficulty of story.difficulties) {
      if (story.modeIds[difficulty] === modeId) {
        return { storyId: story.id, difficulty };
      }
    }
  }

  return { storyId: "modern", difficulty: "easy" };
}

function getModeForStoryDifficulty(storyId: ModeStoryId, difficulty: ModeDifficultyId) {
  const story = getModeStory(storyId);
  const modeId = story.modeIds[difficulty] ?? story.modeIds[story.defaultDifficulty] ?? "draft";
  return modes.find((item) => item.id === modeId) ?? modes[0];
}

function isHiddenOfferMode(modeId: string) {
  return modeId === "draft-hard" || modeId === "nostalgia-hard";
}

function getModeStoryInfo(storyId: ModeStoryId, lang: Language) {
  const t = translations[lang] as any;

  if (storyId === "modern") {
    return { name: t.storyModernName, desc: t.storyModernDesc };
  }

  return { name: t.storyNostalgiaName, desc: t.storyNostalgiaDesc };
}

function getTranslatedModeName(modeId: string, lang: Language) {
  const t = translations[lang] as any;
  if (modeId === "draft") return t.modeDraftName;
  if (modeId === "draft-hard") return t.modeDraftHardName;
  if (modeId === "nostalgia-hard") return t.modeNostalgiaHardName;
  return t.modeNostalgiaName;
}

function getDifficultyInfo(difficulty: ModeDifficultyId, lang: Language) {
  const t = translations[lang] as any;

  if (difficulty === "hard") {
    return { level: "hard", label: t.modeDifficultyHard };
  }

  if (difficulty === "medium") {
    return { level: "medium", label: t.modeDifficultyMedium };
  }

  return { level: "easy", label: t.modeDifficultyEasy };
}

function upsertMeta(attribute: "name" | "property", key: string, content: string) {
  let element = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function getRuntimeSeoCopy(lang: Language, modeName: string) {
  switch (lang) {
    case "it":
      return {
        title: `Serie A 38-0 | ${modeName} - Gioco draft Serie A`,
        description: `Drafta una XI in modalità ${modeName}, bilancia intesa e rarità, poi simula la rincorsa a una stagione imbattuta da 38 partite.`,
      };
    case "es":
      return {
        title: `Serie A 38-0 | ${modeName} - juego de draft de Serie A`,
        description: `Drafta un XI en modo ${modeName}, equilibra química y rareza, y simula una temporada invicta de 38 partidos.`,
      };
    case "pt-BR":
      return {
        title: `Serie A 38-0 | ${modeName} - jogo de draft da Serie A`,
        description: `Drafte um XI no modo ${modeName}, equilibre entrosamento e raridade, e simule uma temporada invicta de 38 jogos.`,
      };
    case "fr":
      return {
        title: `Serie A 38-0 | ${modeName} - jeu de draft Serie A`,
        description: `Drafte un XI en mode ${modeName}, équilibre entente et rareté, puis simule une saison invaincue de 38 matchs.`,
      };
    default:
      return {
        title: `Serie A 38-0 | ${modeName} - Serie A draft game`,
        description: `Draft an XI in ${modeName} mode, balance chemistry and rarity, then simulate a 38-match unbeaten season chase.`,
      };
  }
}

function isLanguage(value: string | null): value is Language {
  return languageOptions.some((option) => option.code === value);
}

function upsertMetaList(attribute: "name" | "property", key: string, values: string[]) {
  document.querySelectorAll(`meta[${attribute}="${key}"]`).forEach((element) => element.remove());
  for (const value of values) {
    const element = document.createElement("meta");
    element.setAttribute(attribute, key);
    element.setAttribute("content", value);
    document.head.appendChild(element);
  }
}

function upsertLink(rel: string, href: string) {
  let element = document.querySelector(`link[rel="${rel}"]:not([hreflang])`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

function upsertAlternate(hreflang: string, href: string) {
  let element = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "alternate");
    element.setAttribute("hreflang", hreflang);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

function shortenPlayerName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return name;
  const secondToLast = parts[parts.length - 2].toLowerCase();
  if (["del", "di", "de", "da", "la", "le", "lo", "van", "von"].includes(secondToLast)) {
    return parts.slice(parts.length - 2).join(" ");
  }
  return parts[parts.length - 1];
}

function createPlayerDisplayNameResolver(players: PlayerSeason[]) {
  const shortCounts = new Map<string, number>();
  const initialCounts = new Map<string, number>();

  for (const player of players) {
    const shortName = shortenPlayerName(player.name);
    const initialName = abbreviateFirstName(player.name, shortName);
    shortCounts.set(normalizeName(shortName), (shortCounts.get(normalizeName(shortName)) ?? 0) + 1);
    initialCounts.set(normalizeName(initialName), (initialCounts.get(normalizeName(initialName)) ?? 0) + 1);
  }

  return (name: string) => {
    const shortName = shortenPlayerName(name);
    if ((shortCounts.get(normalizeName(shortName)) ?? 0) <= 1) {
      return shortName;
    }

    const initialName = abbreviateFirstName(name, shortName);
    if ((initialCounts.get(normalizeName(initialName)) ?? 0) <= 1) {
      return initialName;
    }

    return name;
  };
}

function abbreviateFirstName(name: string, shortName = shortenPlayerName(name)) {
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ?? "";
  return first && first !== shortName ? `${first[0]}. ${shortName}` : shortName;
}
